import faker from 'faker';
import { sum } from 'lodash';
import {
  Box,
  Grid,
  Card,
  Table,
  Divider,
  TableRow,
  Container,
  TableBody,
  TableHead,
  TableCell,
  Typography,
  TableContainer,
  Stack,
  Button,
  TextField,
  OutlinedInput,
  Tooltip
} from '@material-ui/core';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fCurrency } from 'src/utils/formatNumber';
import {
  getLaborTimelogCost,
  getRecipeCostLaborRowComputed,
  resolveInstallLaborServiceUnitCost,
  resolveInstallLaborServiceLineCost,
  resolveJobCostServiceLineTotal,
  isJobCostInstallLaborPayout,
  isJobCostInstallLaborTimelog,
  sumJobCostProductionTimelogLabor,
  sumJobCostProductionTimelogHours,
  includeJobCostServiceRowActual,
  includeJobCostMaterialRowActual,
  getJobCostServiceActualCost,
  resolveEffectiveProductionLabor
} from 'src/utils/laborTimelogCalc';
import {
  getJobCostMaterialCost,
  getCompareExpectedMaterialQty,
  getCompareExpectedMaterialUnitCost,
  includeMaterialOnCompareExpectedBudget
} from 'src/utils/jobCostMaterialCalc';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import { LoadingButton } from '@material-ui/lab';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { empty } from 'src/Helpers/Common';
import { forwardRef } from 'react';
import ReactToPrint from 'react-to-print';
import { useTheme } from '@emotion/react';
import useCompanyProfile from 'src/hooks/useCompanyProfile';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import { Print } from "@material-ui/icons";
import { GetApp } from "@material-ui/icons";



// Styled Components
const RowResultStyle = styled(TableRow)(({ theme }) => ({
  '& td': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  }
}));

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`
  }
}));

const getOtherEntriesTotals = (entries = []) => {
  const list = Array.isArray(entries) ? entries : [];
  return list.reduce(
    (acc, entry) => {
      const qty = Number(entry?.quantity ?? 0);
      const unitCost = Number(entry?.unit_cost ?? 0);
      const unitBill = Number(entry?.unit_bill ?? 0);
      const totalCostRaw = Number(entry?.total_cost);
      const totalBillRaw = Number(entry?.total_bill);
      const billable = entry?.billable === 1 || entry?.billable === '1' || entry?.billable === true;

      const rowCost = Number.isFinite(totalCostRaw) ? totalCostRaw : (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unitCost) ? unitCost : 0);
      const rawBill = Number.isFinite(totalBillRaw) ? totalBillRaw : (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unitBill) ? unitBill : 0);
      const rowBill = billable ? rawBill : 0;

      acc.cost += rowCost;
      acc.bill += rowBill;
      return acc;
    },
    { cost: 0, bill: 0 }
  );
};

const getServiceActualCost = (service, isTMProject = false) => getJobCostServiceActualCost(service, isTMProject);

const getServiceActualDisplayCost = (service, isTMProject = false) => {
  if (!service) return 0;
  if (service.__isRecipeGroup || Number(service.labour_cost_service_id) === 10) {
    if (!isTMProject) {
      const hours = Number(service.jobcost_quantity || 0);
      const enteredCost = Number(service.jobcost_total_cost || 0);
      if (hours <= 0 && enteredCost <= 0) return 0;
    }
    // Backup before production labor display alignment fix:
    // return getServiceActualCost(service, isTMProject);
    if (service.__isRecipeGroup) {
      return Number(service.jobcost_total_cost || 0);
    }
    return getServiceActualCost(service, isTMProject);
  }

  const hours = Number(service.jobcost_quantity || 0);
  if (!isTMProject && hours <= 0) return 0;
  if (isTMProject) return getServiceActualCost(service, isTMProject);

  const lineTotal = resolveJobCostServiceLineTotal(service, false, hours);
  // Backup before additional-services display cap fix:
  // return resolveJobCostServiceLineTotal(service, false, hours);
  if (!isTMProject && service.recipe_id == null && Number(service.labour_cost_service_id) !== 10) {
    const expected = (parseFloat(service.originalCost) || 0) * (parseFloat(service.qty) || 0);
    return expected > 0 ? Math.min(lineTotal, expected) : lineTotal;
  }
  return lineTotal;
};

/** Production Labor table row must match summary effectiveProductionLaborActual, not stacked recipe jobcost. */
const getCompareActualServiceRowCost = (service, effectiveProductionLabor, isTMProject) => {
  if (service?.__isRecipeGroup) {
    // Backup before production labor list/summary alignment fix:
    // return getServiceActualDisplayCost(service, isTMProject);
    return Number(effectiveProductionLabor || service.jobcost_total_cost || 0);
  }
  return getServiceActualDisplayCost(service, isTMProject);
};

/** Used by CompareJobCost and PrintableJobCostReport: install labor row vs timelog-derived total. */
const getActualInstallLaborJobcostTotal = (sameItems, laborCostTotalFromTimelogs, laborHoursTotalFromTimelogs, isTMProject = false) => {
  const rowFilter = (s) => (isTMProject ? includeRecipeCostServiceRow(s) : includeJobCostServiceRowActual(s, false));
  const installRows = (sameItems || []).filter((s) => rowFilter(s));
  const manualRows = installRows.filter((s) => s.jobcost_quantity != null && s.jobcost_quantity !== '' && parseFloat(s.jobcost_quantity) > 0);
  const fromTimelogs = Number(laborCostTotalFromTimelogs || 0);

  if (manualRows.length > 0) {
    const rowCostSum = installRows.reduce((sum, s) => sum + parseFloat(s?.jobcost_total_cost || 0), 0);
    const sumFromServices = installRows.reduce(
      (sum, s) => sum + resolveInstallLaborServiceLineCost(s, isTMProject),
      0
    );
    return Math.max(rowCostSum, sumFromServices).toFixed(2);
  }

  const hoursT = Number(laborHoursTotalFromTimelogs || 0);
  if (hoursT > 0 && fromTimelogs > 0) return fromTimelogs.toFixed(2);
  if (Number.isFinite(fromTimelogs) && fromTimelogs > 0) return fromTimelogs.toFixed(2);
  // OLD (non-T&M): fromServices used getServiceActualCost / total_cost from SO — showed Install $47.50 before cost-card entry.
  if (isTMProject) {
    const fromServices = (sameItems || []).reduce((sum, s) => sum + getServiceActualCost(s, true), 0);
    return fromServices.toFixed(2);
  }
  return '0.00';
};

/** Total clock hours from staff timelogs (same basis as JobCostMemberClockInTimer). */
const getLaborHoursTotalFromTimelogs = (logs) => {
  const list = Array.isArray(logs) ? logs : [];
  return list.reduce((sum, log) => {
    const th = Number(log?.total_hours);
    const tm = Number(log?.total_minutes);
    if (Number.isFinite(th) || Number.isFinite(tm)) {
      const safeH = Number.isFinite(th) ? th : 0;
      const safeM = Number.isFinite(tm) ? tm : 0;
      return sum + safeH + safeM / 60;
    }
    const h = Number(log?.hours) || 0;
    const m = Number(log?.minutes) || 0;
    return sum + (m > 0 ? h + m / 60 : h);
  }, 0);
};

/** Quantity/Hrs for install labor row: prefer timelog hours when any clock time exists. */
const getInstallLaborQtyHours = (sameItems, laborCostTotalFromTimelogs, laborHoursTotalFromTimelogs) => {
  const fromServices = (sameItems || []).reduce((sum, s) => sum + Number(s.jobcost_quantity || s.qty || 0), 0);
  const fromTimelogs = Number(laborHoursTotalFromTimelogs || 0);
  if (fromTimelogs > 0) return fromTimelogs;
  return fromServices;
};

/** Include job-cost service rows in totals when DB has qty/cost or is billable/taken */
const includeRecipeCostServiceRow = (service) => {
  if (!service) return false;
  if (service.taken === 1 || service.taken === '1') return true;
  if (parseFloat(service.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(service.jobcost_total_cost || service.total_cost || 0) > 0) return true;
  if (parseFloat(service.qty || 0) > 0) return true;
  if (parseFloat(service.total_bill || 0) > 0) return true;
  if (parseFloat(service.jobcost_total_bill || 0) > 0) return true;
  return false;
};

const includeRecipeCostMaterialRow = (material) => {
  if (!material) return false;
  if (material.taken === 1 || material.taken === '1') return true;
  if (parseFloat(material.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(material.jobcost_total || 0) > 0) return true;
  if (parseFloat(material.quantity || 0) > 0) return true;
  if (parseFloat(material.total_bill || 0) > 0) return true;
  if (parseFloat(material.jobcost_total_bill || 0) > 0) return true;
  return false;
};

/** Expected-budget list: skip job-added additional services only (keep install/production labor). */
const skipServiceOnExpectedBudgetList = (service) =>
  Number(service?.from_job) === 1 &&
  Number(service?.labour_cost_service_id) !== 10 &&
  service?.recipe_id == null;

/** T&M job-cost materials are actual-only; never belong on Expected budget. */
// Backup before T&M expected material edit fix: relied on notFromRecipe + jobcost_actual_quantity > 0 only (T&M materials leaked into Expected after edit).
// Backup before non-T&M formula expected fix: Number(material.jobcost_actual_quantity || 0) > 0 — missed recipe materials with quantity only.
const includeMaterialOnExpectedBudget = (material, isTMProject = false) =>
  includeMaterialOnCompareExpectedBudget(material, isTMProject);

// Backup before T&M expected material edit fix: .filter((curr) => curr.notFromRecipe != true)
// Backup before T&M sales order material expected fix:
// const includeMaterialOnExpectedPayout = (material, isTMProject = false) => {
//   if (!material || isTMProject) return false;
//   return material.notFromRecipe != true;
// };
const includeMaterialOnExpectedPayout = (material, isTMProject = false) => {
  if (!material) return false;
  if (material.notFromRecipe == true) return false;
  if (isTMProject) {
    const expectedQty = Number(material?.jobcost_actual_quantity ?? material?.quantity ?? 0);
    return expectedQty > 0;
  }
  return true;
};

const getCompareExpectedServiceUnitCost = (service, isTMProject = false) => {
  if (!service) return 0;
  // T&M Install Labor expected cost should use markup catalog rate.
  // Backup before T&M expected Install Labor markup fix:
  // return Number(service.originalCost || service.unit_cost || 0);
  if (isTMProject && Number(service.labour_cost_service_id) === 10) {
    return resolveInstallLaborServiceUnitCost(service, true);
  }
  return Number(service.originalCost || service.unit_cost || 0);
};

const includeRecipeCostStockRow = (stock) => {
  if (!stock) return false;
  if (stock.taken === 1 || stock.taken === '1') return true;
  if (parseFloat(stock.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(stock.jobcost_total_cost || 0) > 0) return true;
  if (parseFloat(stock.location_quantity || 0) > 0) return true;
  if (parseFloat(stock.total_bill || 0) > 0) return true;
  if (parseFloat(stock.jobcost_total_bill || 0) > 0) return true;
  return false;
};

export default function CompareJobCost({ id, refreshTrigger, staffTimelogsProp, laborTabLaborCost = 0 }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const componentRef = useRef();
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedProjects1, setSelectedProjects1] = useState([]);
  const [materialPayOut, setMaterialPayOut] = useState(0);
  const [materialPayOut1, setMaterialPayOut1] = useState(0);
  const [manualTotalPayOut, setManualTotalPayOut] = useState(0);
  const [hourlyTotalPayOut, setHourlyTotalPayOut] = useState(0);
  const [excessHourlyPayoutAmount, setExcessHourlyPayoutAmount] = useState(0)
  const [excessManualPayoutAmount, setExcessManualPayoutAmount] = useState(0)
  const [additionalServices, setAdditionalServices] = useState([])
  const [projectData, setProjectData] = useState([])
  const [recipe, setRecipe] = useState([])
  // const [services, setServices] = useState([]);
  const [services, setAllServices] = useState([]);
  const [stockProducts, setStockProducts] = useState([])
  const [jobCost, setJobCost] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialMaterials, setInitialMaterials] = useState([]);
  const [productList, setProductList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const params = localStorage.getItem("params");
  const user = JSON.parse(window.localStorage.getItem('user'))
  const [allPayouts, setAllPayouts] = useState([])
  const [manuallyAddedPayouts, setManuallyAddedPayouts] = useState([])
  const [totalManualHours, setTotalManualHours] = useState(0);
  const [staffTimelogsGet, setStaffTimelogsGet] = useState([])
  const timelogsForCalc = useMemo(
    () => (staffTimelogsProp != null ? staffTimelogsProp : staffTimelogsGet),
    [staffTimelogsProp, staffTimelogsGet]
  );
  const [otherEntries, setOtherEntries] = useState([])

  const laborCostTotalFromTimelogs = useMemo(() => {
    return (timelogsForCalc || []).reduce((sum, log) => sum + getLaborTimelogCost(log), 0);
  }, [timelogsForCalc]);

  const laborHoursTotalFromTimelogs = useMemo(
    () => getLaborHoursTotalFromTimelogs(timelogsForCalc),
    [timelogsForCalc]
  );

  const installLaborMetrics = useMemo(() => {
    const logs = timelogsForCalc || [];
    let costTotal = 0;
    let billTotal = 0;
    let hoursTotal = 0;
    
    // Find the Install Labor service rate for fallback
    // Backup before T&M category type fix: const isTM = projectData?.project_category === 12;
    const isTM = Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
    const installLaborService = (services || []).find(s => Number(s.labour_cost_service_id) === 10);
    // OLD: const serviceRate = Number(installLaborService?.jobcost_unit_cost || installLaborService?.originalCost || installLaborService?.unit_cost || 0);
    const serviceRate = resolveInstallLaborServiceUnitCost(installLaborService, isTM);
    const serviceBillRate = Number(installLaborService?.bill_unit_price || installLaborService?.unit_bill || 0);

    logs.forEach(log => {
      // const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
      // const isDriver = log?.isDriver === 1 || log?.isDriver === '1' || log?.is_driver === 1 || log?.is_driver === '1';
      // const taskName = (log?.task_name || '').toString().trim().toUpperCase();
      // const isManual = log?.is_manually_added === 1 || log?.is_manually_added === '1';
      
      // const isDefaultLabor = sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver || !isManual));
      
      // if (isDefaultLabor) {
      //   // OLD: const fallbackR = isTM ? serviceRate : 0;
      //   const fallbackR = serviceRate;
      //   const fallbackBR = isTM ? serviceBillRate : 0;
      //   const computed = getRecipeCostLaborRowComputed(log, fallbackR, fallbackBR);
      //   costTotal += (Number(computed?.cost) || 0);
        
      //   // Match logic from getLaborHoursTotalFromTimelogs
      //   const th = Number(log?.total_hours);
      //   const tm = Number(log?.total_minutes);
      //   if (Number.isFinite(th) || Number.isFinite(tm)) {
      //     const safeH = Number.isFinite(th) ? th : 0;
      //     const safeM = Number.isFinite(tm) ? tm : 0;
      //     hoursTotal += (safeH + safeM / 60);
      //   } else {
      //     const h = Number(log?.hours) || 0;
      //     const m = Number(log?.minutes) || 0;
      //     hoursTotal += (m > 0 ? h + m / 60 : h);
      //   }
      if (!isJobCostInstallLaborTimelog(log, services)) return;

      // OLD: const fallbackR = isTM ? serviceRate : 0;
      const fallbackR = serviceRate;
      const fallbackBR = isTM ? serviceBillRate : 0;
      const computed = getRecipeCostLaborRowComputed(log, fallbackR, fallbackBR);
      costTotal += (Number(computed?.cost) || 0);
      
      // Match logic from getLaborHoursTotalFromTimelogs
      const th = Number(log?.total_hours);
      const tm = Number(log?.total_minutes);
      if (Number.isFinite(th) || Number.isFinite(tm)) {
        const safeH = Number.isFinite(th) ? th : 0;
        const safeM = Number.isFinite(tm) ? tm : 0;
        hoursTotal += (safeH + safeM / 60);
      } else {
        const h = Number(log?.hours) || 0;
        const m = Number(log?.minutes) || 0;
        hoursTotal += (m > 0 ? h + m / 60 : h);
      }

      const isBillable = log?.billable === 1 || log?.billable === true || log?.is_billable === 1 || log?.is_billable === true;
      if (isBillable) {
        billTotal += (Number(computed?.bill) || 0);
      }
    });
    
    return {
      cost: costTotal,
      bill: billTotal,
      hours: hoursTotal
    };
  }, [timelogsForCalc, services, projectData?.project_category]);

    const [data, setData] = useState({});
    const fetchDetails = async () => {
      try {
        const res = await axios.post(`${REST_API_END_POINT}settings/get-companyProfile-details`);
        if (res.data.status === 1) {
          setData(...res.data.data);
        }
      } catch (error) {
        console.log('get-companyProfile-details error : ', error);
      }
    };

  const fetchFinishedProductsData = async () => {
    try {
      const finishedProductsResponse = await axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-add-materials-finished-products`);
      return Array.isArray(finishedProductsResponse?.data?.results)
        ? finishedProductsResponse.data.results
        : [];
    } catch (error) {
      console.error('Error fetching finished products:', error);
      return [];
    }
  };

  const fetchData = () => {
    try {
      setLoading(true)
      // Backup (before product-formula compare fix — mapped with empty productList on first load):
      // const jobCostRequest = axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-recipe`,
      //   { userId: user.user_id,userType: user.user_type, projectId: id, orderId: params });
      // jobCostRequest.then((response) => {
      //   const productListData = Array.isArray(productList) ? productList : [];
      //   fetchFinishedProductsData()
      //     .then((freshProductListData) => {
      //       if (Array.isArray(freshProductListData) && freshProductListData.length > 0) {
      //         setProductList(freshProductListData);
      //       }
      //     })
      //     .catch((error) => console.error('Error refreshing finished products:', error));
      Promise.all([
        axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-recipe`,
          { userId: user.user_id, userType: user.user_type, projectId: id, orderId: params }),
        fetchFinishedProductsData()
      ]).then(([response, freshProductListData]) => {
        const productListData = Array.isArray(freshProductListData) && freshProductListData.length > 0
          ? freshProductListData
          : (Array.isArray(productList) ? productList : []);
        if (productListData.length > 0) {
          setProductList(productListData);
        }
        let selectedProducts = response.data.selectedMaterials
        // let selectedProducts = [
        //   ...(response.data.selectedMaterials || []), 
        //   ...(response.data.materials || [])
        // ];
        let receivedRfpProducts=selectedProducts.filter(row=>row.rfp===1)
        let rfpProducts = receivedRfpProducts.map(product => ({
            ...product,
            jobcost_unit_cost: product.unit_cost,
            jobcost_quantity: product.quantity,
            jobcost_actual_unit_cost: product.actual_unit_cost,
            jobcost_actual_quantity: product.actual_quantity
        }));
        selectedProducts=selectedProducts.filter(row=>row.rfp===0)
        let initialData = response.data.selectedMaterials
        const allPayouts = response.data.allPayOut
        const project = response.data.projects
        const allMaterials = response.data.allMaterials
        const allRecipes = response.data.allRecipies
        const material = response.data.materials
        const fetchedOtherEntries = response?.data?.otherEntries || [];
        let allServices = response.data.allServices
        let stockProducts = response?.data?.stockProducts
        let additionalServices = response.data.additionalServices
        if (additionalServices) {
          setAdditionalServices(additionalServices.reduce((accumulator, currentValue) => {
            return accumulator + (currentValue?.jobcost_unit_cost * currentValue?.jobcost_quantity);
          }, 0))
        }
        if (project) {
          setProjectData(project)
        }
        if (allRecipes) {
          setRecipe(allRecipes)
        }
        if (response.data.newstafftimelogs) {
          setStaffTimelogsGet(response.data?.newstafftimelogs);
        }
        if (allServices) {
          setAllServices(allServices)
        }
        setOtherEntries(fetchedOtherEntries);
        if (stockProducts) {
          setStockProducts(stockProducts)
        }
        if (allPayouts?.length > 0) {
          let totalPayOut = allPayouts?.reduce((acc, curr) => acc + curr.amount, 0)
          const totalHourlyPayoutAmount = allPayouts?.filter(payout => payout.is_auto_pay === 1 && (payout.service_id === null || payout.service_id === undefined || payout.service_id === 0 || payout.service_id === "0") && payout.is_manually_added !== 1)?.reduce((acc, curr) => acc + curr.amount, 0);
          setExcessHourlyPayoutAmount(totalHourlyPayoutAmount)
          const totalManualPayoutAmount = allPayouts?.filter(payout => payout.is_auto_pay !== 1 && (payout.service_id === null || payout.service_id === undefined || payout.service_id === 0 || payout.service_id === "0") && payout.is_manually_added !== 1)?.reduce((acc, curr) => acc + curr.amount, 0);
          setExcessManualPayoutAmount(totalManualPayoutAmount)

          const summedHourlyPayouts = Object.values(
            allPayouts?.filter(payout => payout.is_auto_pay === 1)?.reduce((acc, payout) => {
              // Create a unique key based on project_id, task_id, and team
              const key = `${payout.project_id}-${payout.task_id || "null"}-${payout.team || "null"}`;
              
              // Initialize the group in the accumulator if it doesn't exist
              if (!acc[key]) {
                acc[key] = {
                  project_id: payout.project_id,
                  task_id: payout.task_id,
                  team: payout.team,
                  total_amount: 0,
                };
              }
          
              // Add the current payout amount to the total for this group, rounded to 2 decimals
              acc[key].total_amount = parseFloat((acc[key].total_amount + payout.amount).toFixed(2));
          
              return acc;
            }, {})
          );
          
          console.log('summedHourlyPayouts===',summedHourlyPayouts);

          const summedManualPayouts = Object.values(
            allPayouts?.filter(payout => payout.is_auto_pay !== 1)?.reduce((acc, payout) => {
              // Create a unique key based on project_id, task_id, and team
              const key = `${payout.project_id}-${payout.task_id || "null"}-${payout.team || "null"}`;
              
              // Initialize the group in the accumulator if it doesn't exist
              if (!acc[key]) {
                acc[key] = {
                  project_id: payout.project_id,
                  task_id: payout.task_id,
                  team: payout.team,
                  total_amount: 0,
                };
              }
          
              // Add the current payout amount to the total for this group, rounded to 2 decimals
              acc[key].total_amount = parseFloat((acc[key].total_amount + payout.amount).toFixed(2));
          
              return acc;
            }, {})
          );
          
          console.log('summedManualPayouts===',summedManualPayouts);
          
          setHourlyTotalPayOut(summedHourlyPayouts?.reduce((acc, curr) => acc + curr.total_amount, 0))
          setManualTotalPayOut(summedManualPayouts?.reduce((acc, curr) => acc + curr.total_amount, 0))
          // setHourlyTotalPayOut(allPayouts.filter(payout => payout.is_auto_pay === 1).reduce((acc, curr) => acc + curr.amount, 0))
          // setManualTotalPayOut(allPayouts.filter(payout => payout.is_auto_pay !== 1).reduce((acc, curr) => acc + curr.amount, 0))
        }
        if (allPayouts) {
          setAllPayouts(allPayouts || [])
          
          // Store only manually added payouts in separate state
          const fetchedIsTM = Number(project?.project_category) === 12 || project?.job_category_name === 'Time & Material';
          const manualPayouts = allPayouts.filter(
            // Backup before T&M production labor fix: p => (p.is_manually_added === 1 || p.is_manually_added === "1") && (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id)
            // Backup before Install Labor request-service classification:
            // p => (p.is_manually_added === 1 || p.is_manually_added === "1") &&
            //      (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id) &&
            //      (!fetchedIsTM || !isJobCostInstallLaborPayout(p))
            p => (p.is_manually_added === 1 || p.is_manually_added === "1") && 
                 (p.service_id === null || p.service_id === undefined || p.service_id === 0 || p.service_id === "0" || !p.service_id) &&
                 (!fetchedIsTM || !isJobCostInstallLaborPayout(p, services))
          );
          setManuallyAddedPayouts(manualPayouts || []);
        }
        if (initialData) {
          setInitialMaterials(initialData)
        }
        if (productListData) {
          setProductList(productListData)
        }
        if (allMaterials) {
          setMaterialList(allMaterials)
        }
        if (project) {
          setJobCost(project.cost || 0)
        }
        if (selectedProducts?.length > 0 || material?.length > 0) {
          const list = Array.isArray(productListData) ? productListData : [];
          const selectedProjects1 = (selectedProducts || [])?.map(selectedProduct => {
            const matchingProduct = productListData.find(product => product.id === selectedProduct.product_id);
            if (matchingProduct) {
              return {
                ...matchingProduct,
                product_id: selectedProduct.product_id,
                jobcost_quantity: selectedProduct.quantity,
                jobcost_unit_cost: selectedProduct.unit_price,
                jobcost_total: selectedProduct.total,
                jobcost_actual_quantity: selectedProduct.actual_quantity,
                jobcost_actual_unit_cost: selectedProduct.actual_unit_price,
                markup_percent: selectedProduct.markup_percent,
                material_markup_percentage: selectedProduct.material_markup_percentage,
                product_markup: selectedProduct.product_markup,
                // Backup before T&M graph material fix: markup fallback came only from matchingProduct.
                markup: selectedProduct.markup || selectedProduct.retail_price || matchingProduct?.markup,
                retail_price: selectedProduct.retail_price || matchingProduct?.retail_price,
                recipeName: selectedProduct.recipeName,
                taken:selectedProduct.taken,
                task_id:selectedProduct.task_id
              };
            }
            return null; // Handle cases where no matching product is found
          }).filter(project => project !== null);
          const selectedProjects2 = (material || []).map(selectedProduct => {
            const matchingProduct = productListData.find(product => product.id === selectedProduct.product_id);
            if (matchingProduct) {
              return {
                ...matchingProduct,
                product_id: selectedProduct.product_id,
                jobcost_quantity: selectedProduct.quantity,
                jobcost_unit_cost: selectedProduct.unit_price,
                jobcost_total: selectedProduct.total,
                jobcost_actual_quantity: selectedProduct.actual_quantity,
                jobcost_actual_unit_cost: selectedProduct.actual_unit_price,
                markup_percent: selectedProduct.markup_percent,
                material_markup_percentage: selectedProduct.material_markup_percentage,
                product_markup: selectedProduct.product_markup,
                // Backup before T&M graph material fix: markup fallback came only from matchingProduct.
                markup: selectedProduct.markup || selectedProduct.retail_price || matchingProduct?.markup,
                retail_price: selectedProduct.retail_price || matchingProduct?.retail_price,
                recipeName: selectedProduct.recipeName,
                taken:selectedProduct.taken,
                task_id:selectedProduct.task_id
              };
            }
            return null; // Handle cases where no matching product is found
          }).filter(project => project !== null);

          // recipeCost.js parity: job_cost recipe=0 rows often use product_id not in finished-product list (notFromRecipe).
          // They were dropped here so Material Cost stayed $0; synthesize rows for compare tables + payout sums.
          const orphanAsProjects = (material || [])
            .filter((m) => !list.some((p) => p.id === m.product_id))
            .map((m) => {
              const qty = Number(m.quantity) || 0;
              const actualQty = Number(m.actual_quantity ?? m.quantity) || 0;
              const unit = Number(m.unit_price) || 0;
              const actualUnit = Number(m.actual_unit_price ?? m.unit_price) || 0;
              return {
                id: m.product_id,
                name: m.productName || m.rfpProductName || 'Material',
                product_id: m.product_id,
                jobcost_quantity: qty,
                jobcost_unit_cost: unit,
                jobcost_total: parseFloat(m.total) || 0,
                jobcost_actual_quantity: actualQty,
                jobcost_actual_unit_cost: actualUnit,
                markup_percent: m.markup_percent,
                material_markup_percentage: m.material_markup_percentage,
                product_markup: m.product_markup,
                // Backup before T&M graph material fix: orphan material rows did not carry retail_price/markup.
                markup: m.markup || m.retail_price || 0,
                retail_price: m.retail_price,
                recipeName: m.recipeName || '',
                taken: m.taken,
                task_id: m.task_id,
                notFromRecipe: false,
                billable: m.billable ?? 1
              };
            });

          const selectedProjects2Merged = [...(selectedProjects2 || []), ...orphanAsProjects];
          setSelectedProjects([...selectedProjects1,...rfpProducts]);
          setSelectedProjects1(selectedProjects2Merged);

          // Backup before T&M category type fix: const fetchedIsTM = Number(project?.project_category) === 12;
          const fetchedIsTM = Number(project?.project_category) === 12 || project?.job_category_name === 'Time & Material';
          const materialPayOut = selectedProjects2Merged
            // Backup before T&M expected material edit fix: .filter((curr) => curr.notFromRecipe != true)
            .filter((curr) => includeMaterialOnExpectedPayout(curr, fetchedIsTM))
            .reduce(
              (acc, curr) =>
                // Backup before T&M material markup: acc + (Number(curr.jobcost_actual_quantity) || 0) * (Number(curr.jobcost_actual_unit_cost) || 0)
                acc + getJobCostMaterialCost(curr, fetchedIsTM, {
                  qty: curr.jobcost_actual_quantity,
                  unitCost: curr.jobcost_actual_unit_cost
                }),
              0
            );

          const materialPayOut1 = selectedProjects2Merged
            // Backup before T&M sales order material actual fix:
            // .filter((curr) => curr.notFromRecipe != true)
            .filter((curr) => curr.notFromRecipe != true && includeJobCostMaterialRowActual(curr, false))
            // Backup before T&M material markup: .reduce((acc, curr) => acc + (Number(curr.jobcost_total) || 0), 0);
            .reduce((acc, curr) => acc + getJobCostMaterialCost(curr, fetchedIsTM), 0);

          console.log('Material PayOut (Not From Recipe):', materialPayOut);
          console.log('Material PayOut1 (From Recipe):', materialPayOut1);

          setMaterialPayOut(materialPayOut);
          setMaterialPayOut1(materialPayOut1);

        }
      })  
    }
    catch (error) {
      console.error("Error fetching data:", error);
    }finally{
      setTimeout(() => {
        setLoading(false)
      }, 500);
    }
  }

  useEffect(() => {
    fetchData();
    fetchDetails()
  }, [id, refreshTrigger]);

    useEffect(() => {
    const totalHours = timelogsForCalc
      ?.filter(log => log.is_manually_added === 1 || log.is_manually_added === "1")
      // Backup before non-T&M Install Labor production split:
      // ?.reduce((accumulator, currentValue) => {
      //   return accumulator + (parseFloat(currentValue.hours) || 0);
      // }, 0) || 0;
      ?.filter(log => !isJobCostInstallLaborTimelog(log, services))
      ?.reduce((accumulator, currentValue) => {
        return accumulator + (parseFloat(currentValue.hours) || 0);
      }, 0) || 0;
    setTotalManualHours(totalHours);
  }, [timelogsForCalc, services]);

  // Calculate manual hours cost
  const manualHoursCost = useMemo(() => {
    return timelogsForCalc
      ?.filter(log => log.is_manually_added === 1 || log.is_manually_added === "1")
      // Backup before non-T&M Install Labor production split:
      // ?.reduce((sum, log) => sum + (parseFloat(log.amount) || 0), 0) || 0;
      ?.filter(log => !isJobCostInstallLaborTimelog(log, services))
      ?.reduce((sum, log) => sum + (parseFloat(log.amount) || 0), 0) || 0;
  }, [timelogsForCalc, services]);

  const totalStockCost = useMemo(() => (
  stockProducts.reduce((sum, item) => {
    const cost = parseFloat(item.location_quantity || 0) * parseFloat(item.product_retail_price || 0);
    return sum + cost;
  }, 0)
), [stockProducts]);
console.log('totalStockCosttotalStockCost 1',totalStockCost)

const totalStockCost1 = useMemo(() => (
  stockProducts.reduce((sum, item) => {
    if (!includeRecipeCostStockRow(item)) return sum;

    const cost = parseFloat(item.jobcost_total_cost || 0);
    return sum + cost;
  }, 0)
), [stockProducts]);
console.log('totalStockCosttotalStockCost 2',totalStockCost1)

  // Backup before T&M material markup fix: const isTMProject = projectData?.project_category === 12;
  const isTMProject = Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
  const manuallyAddedPayoutsTotal = useMemo(() => {
    return manuallyAddedPayouts?.reduce(
      (sum, p) => sum + parseFloat(p.amount || 0), 0
    ) || 0;
  }, [manuallyAddedPayouts]);
  const productionManualPayoutsTotal = useMemo(() => {
    // Backup before T&M category type fix: if (projectData?.project_category === 12) {
    if (isTMProject) {
      const manuallyAddedPayoutsForTM = allPayouts?.filter(
        // Backup before T&M production labor fix: p => (p.is_manually_added === 1 || p.is_manually_added === "1")
        // Backup before Install Labor request-service classification:
        // p => (p.is_manually_added === 1 || p.is_manually_added === "1") && !isJobCostInstallLaborPayout(p)
        p => (p.is_manually_added === 1 || p.is_manually_added === "1") && !isJobCostInstallLaborPayout(p, services)
      ) || [];
      return manuallyAddedPayoutsForTM.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
    }
    // Backup before non-T&M Install Labor production split:
    // return manuallyAddedPayoutsTotal;
    return manuallyAddedPayouts?.filter(
      p => !isJobCostInstallLaborPayout(p, services)
    )?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
  // Backup before T&M production labor fix: }, [projectData?.project_category, allPayouts, manuallyAddedPayoutsTotal]);
  // Backup before Install Labor request-service classification:
  // }, [isTMProject, allPayouts, manuallyAddedPayoutsTotal]);
  }, [isTMProject, allPayouts, manuallyAddedPayoutsTotal, manuallyAddedPayouts, services]);

  const otherTotals = useMemo(() => getOtherEntriesTotals(otherEntries), [otherEntries]);
  const installLaborRowCostSum = (services || []).reduce((sum, s) => {
    if (Number(s?.labour_cost_service_id) !== 10) return sum;
    // Backup before non-T&M actual-only fix: includeRecipeCostServiceRow(s)
    if (!includeJobCostServiceRowActual(s, isTMProject)) return sum;
    return sum + parseFloat(s?.jobcost_total_cost || 0);
  }, 0);

  const materialTotal = useMemo(() => {
    return (selectedProjects || []).reduce((sum, proj) => {
      // Backup before T&M material markup: return sum + (Number(proj.jobcost_actual_unit_cost || proj.unit_cost || 0) * Number(proj.jobcost_actual_quantity || 0));
      return sum + getJobCostMaterialCost(proj, isTMProject, {
        // Backup before non-T&M formula expected fix: qty: proj.jobcost_actual_quantity only.
        qty: getCompareExpectedMaterialQty(proj),
        unitCost: getCompareExpectedMaterialUnitCost(proj)
      });
    }, 0).toFixed(2);
  // Backup before T&M material markup: }, [selectedProjects]);
  }, [selectedProjects, isTMProject]);

  const materialTotal1 = useMemo(() => {
    return (selectedProjects || []).filter(i => (
      // Backup before T&M sales order material actual fix:
      // includeJobCostMaterialRowActual(i, isTMProject) ||
      // (isTMProject && (includeRecipeCostMaterialRow(i) || i.billable == 1))
      includeJobCostMaterialRowActual(i, false)
    )).reduce((acc, project) => {
      // Backup before non-T&M actual-only fix: includeRecipeCostMaterialRow(i) || i.billable == 1
      // Backup before T&M material markup: const total = parseFloat(project?.jobcost_total || (project?.vendor_price || project?.unit_cost || 0) * project?.jobcost_quantity || 0); return acc + total;
      return acc + getJobCostMaterialCost(project, isTMProject);
    }, 0).toFixed(2);
  // Backup before T&M material markup: }, [selectedProjects]);
  }, [selectedProjects, isTMProject]);



  const additionalLaborCost = useMemo(() => (
    services.reduce((sum, service) => {
      if (Number(service.from_job) === 1) return sum;
      if (service.labour_cost_service_id === 10) return sum;
      if (service.recipe_id == null) {
        const total = (parseFloat(service.qty || 0) * parseFloat(service.originalCost || service.unit_cost || 0));
        return sum + total;
      }
      return sum;
    }, 0)
  ), [services]);

    const excessLabourOnly = useMemo(() => {
    let labourExpected = 0;
    let labourActual = 0;
  
    services.forEach(service => {
      if (+service.labour_cost_service_id === 10) {
        const expected = parseFloat(service.originalCost) || 0;
        const actual = parseFloat(service.jobcost_total_cost) || 0;
        labourExpected += expected;
        labourActual += actual;
      }
    });
  
    return labourActual > labourExpected
      ? +(labourActual - labourExpected).toFixed(2)
      : 0;
  }, [services]);

  console.log('additionalLaborCost=====',additionalLaborCost)
  console.log('services=====',services)
  console.log('selectedProjects=====',selectedProjects)
  // const additionalLaborCost1 = useMemo(() => (
  //   services.reduce((sum, service) => {
  //     if (service.labour_cost_service_id === 10) return sum;
  //     // if(projectData?.job_category_name=='Time & Material'){
  //     //   const jobcost_total_cost = parseFloat(service.jobcost_total_cost || 0);
  //     //   if (service.recipe_id == null && service.taken == 1) {
  //     //     return sum + jobcost_total_cost;
  //     //   }else{
  //     //     return sum + 0
  //     //   }
  //     // }else{
  //       const quantity = parseFloat(service.jobcost_quantity || service.qty || 0);
  //       const unitCost = parseFloat(service.jobcost_unit_cost || service.originalCost || service.unit_cost || 0);
  //       const jobcost_total_cost = parseFloat(service.jobcost_total_cost || 0);
  //       if (service.recipe_id == null && service.labour_cost_service_id == 10 && service.taken == 1) {
  //         return sum + jobcost_total_cost;
  //       }else if (service.recipe_id == null && service.taken == 1) {
  //         // return sum + (quantity * unitCost);
  //         return sum + jobcost_total_cost;
  //       }else{
  //         return sum + 0
  //       }
  //     // }
  //     // if (service.recipe_id == null) {
  //     //   return sum + service.jobcost_unit_cost * service.jobcost_quantity;
  //     // }
  //     return sum;
  //   }, 0)
  // ), [services]);

  // const productFormulaCost = useMemo(() => (
  //   selectedProjects?.reduce((sum, proj) => sum + (proj.jobcost_actual_unit_cost || proj.unit_cost) * proj.jobcost_actual_quantity, 0) +
  //   services.reduce((sum, serv) => (serv.recipe_id ? sum + serv.jobcost_unit_cost * serv.jobcost_quantity : sum), 0)
  // ), [selectedProjects, services]);

    const additionalLaborCost1 = useMemo(() => (
    services.reduce((sum, service) => {
      // Backup before JobCost-aligned additional services fix:
      // if (!includeJobCostServiceRowActual(service, isTMProject) || service.recipe_id != null || service.labour_cost_service_id === 10) return sum;
      // const actual = getJobCostServiceActualCost(service, isTMProject);
      if (!includeRecipeCostServiceRow(service) || service.recipe_id != null || service.labour_cost_service_id === 10) return sum;
      const actual = parseFloat(service.jobcost_total_cost || 0);
      const expected = (parseFloat(service.originalCost) || 0) * (parseFloat(service.qty) || 0);
      // Backup before T&M category type fix: return sum + (projectData?.project_category !== 12 && expected > 0 ? Math.min(actual, expected) : actual);
      return sum + (!isTMProject && expected > 0 ? Math.min(actual, expected) : actual);
    }, 0)
  // Backup before T&M category type fix: ), [services, projectData?.project_category]);
  ), [services, isTMProject]);
  
  // const productFormulaCost = useMemo(() => (
  //   (selectedProjects?.reduce(
  //     (sum, proj) => sum + (Number(proj.jobcost_actual_unit_cost) || Number(proj.unit_cost)) * Number(proj.jobcost_actual_quantity), 
  //     0
  //   ) || 0) +
  //   (services?.reduce(
  //     (sum, serv) => ((serv.recipe_id != null || serv?.labour_cost_service_id === 10) ? sum + serv.originalCost * serv.qty : sum), 
  //     0
  //   ) || 0)
  // ), [selectedProjects, services]);

  const productFormulaCost = useMemo(() => {
    const filtered = services?.filter(
      s => s?.recipe_id && s?.labour_cost_service_id !== 10 && Number(s.from_job) !== 1
    );

    // const expected = filtered.reduce(
    //   (sum, s) => sum + Number(s.originalCost || 0) * Number(s.qty || 0), 0
    // );
    // const actual = filtered.reduce(
    //   (sum, s) => sum + parseFloat(s.jobcost_total_cost || 0), 0
    // );

    // const used = Math.min(expected, actual);
    const expected = filtered.reduce((sum, s) => {
      return sum + Number(s.originalCost || 0) * Number(s.qty || 0);
    }, 0);

  return expected.toFixed(2);

    // return used.toFixed(2);
  }, [services]);


  // const productFormulaCost = useMemo(() => {
  //   return services?.reduce((acc, s) => {
  //     if (s?.recipe_id && s?.labour_cost_service_id !== 10 ) {
  //       const expected = Number(s.originalCost || 0) * Number(s.qty || 0);
  //       const actual = parseFloat(s.jobcost_total_cost || 0);
  //       const cost = expected > actual ? actual : expected;
  //       return acc + cost;
  //     }
  //     return acc;
  //   }, 0).toFixed(2);
  // }, [services]);

  // const materialTotal = useMemo(() => {
  //   return selectedProjects?.reduce((sum, proj) => {
  //     const unitCost = Number(proj.jobcost_actual_unit_cost || proj.unit_cost || 0);
  //     const quantity = Number(proj.jobcost_actual_quantity || 0);
  //     return sum + (unitCost * quantity);
  //   }, 0).toFixed(2);
  // }, [selectedProjects]);



console.log('materialCostmaterialCost',materialTotal1)
  // const productFormulaCost1 = useMemo(() => (
  //   selectedProjects?.filter(i => i.taken==1)?.reduce((sum, proj) => sum + proj.jobcost_total, 0) +
  //   services?.filter(i => i.taken==1)?.reduce((sum, serv) => (serv.recipe_id ? sum + serv.jobcost_unit_cost * serv.jobcost_quantity : sum), 0)
  // ), [selectedProjects, services]);
  console.log('selectedProjects=1===',selectedProjects);
  console.log('selectedProjects=2===',services);

  // const productFormulaCost1 = useMemo(() => (
  //   (selectedProjects?.reduce((accumulator, currentValue) => {
  //     let newTotal = 0;
  //     if(currentValue?.taken == 1){
  //       // newTotal = currentValue?.jobcost_total
  //       const initialCost = currentValue?.originalCost * currentValue?.qty
  //       newTotal = (initialCost > parseFloat(currentValue?.jobcost_total_cost || 0)) ? parseFloat(currentValue?.jobcost_total_cost || 0) : initialCost
  //       // newTotal = currentValue?.jobcost_unit_cost * currentValue?.jobcost_quantity // currentValue?.unit_cost * currentValue?.quantity
  //     }
  //     return accumulator + newTotal;
  //   }, 0) + services?.reduce((accumulator, currentValue) => {
  //     let newTotal = 0;
  //     if((currentValue.recipe_id || currentValue?.labour_cost_service_id === 10) && currentValue?.taken == 1){
  //       // newTotal = parseFloat(currentValue?.jobcost_total_cost || 0)
  //       const initialCost = currentValue?.originalCost * currentValue?.qty
  //       newTotal = (initialCost > parseFloat(currentValue?.jobcost_total_cost || 0)) ? parseFloat(currentValue?.jobcost_total_cost || 0) : initialCost
  //       // newTotal = currentValue?.jobcost_unit_cost * currentValue?.jobcost_quantity // currentValue?.originalCost * currentValue?.qty
  //     }
  //     return accumulator + newTotal;
  //   }, 0)).toFixed(2) // - excessLabourOnly || 0
  //   // (selectedProjects?.reduce(
  //   //   (sum, proj) => (proj.taken == 1 && !empty(proj.recipeName) ? sum + proj.jobcost_total : sum), 
  //   //   0
  //   // ) || 0) +
  //   // (services?.reduce(
  //   //   (sum, serv) => (serv.taken == 1 && (serv.recipe_id != null || serv?.labour_cost_service_id === 10) ? sum + serv.jobcost_unit_cost * serv.jobcost_quantity : sum), 
  //   //   0
  //   // ) || 0)
  // ), [selectedProjects, services]);

    const productFormulaCost1 = useMemo(() => {
    const filtered = services?.filter(
      s => s?.recipe_id && s?.labour_cost_service_id !== 10 && includeJobCostServiceRowActual(s, isTMProject)
    );

    // OLD: parseFloat(s.jobcost_total_cost) || parseFloat(s.total_cost) — formula total_cost counted as actual before cost-card entry.
    const actualTotal = filtered.reduce((sum, s) => sum + getJobCostServiceActualCost(s, isTMProject), 0);
    const expectedTotal = filtered.reduce((sum, s) => sum + (parseFloat(s.originalCost || 0) * parseFloat(s.qty || 0)), 0);
    
    // Split into standard part and excess part if T&M
    // Backup before T&M category type fix: const standardPart = (projectData?.project_category !== 12 && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;
    const standardPart = (!isTMProject && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;
    const used = standardPart + productionManualPayoutsTotal;

    return used.toFixed(2);
  // Backup before T&M category type fix: }, [services, productionManualPayoutsTotal, projectData?.project_category]);
  }, [services, productionManualPayoutsTotal, isTMProject]);
  // Backup before T&M category type fix: const isTMProject = projectData?.project_category === 12;

  const productionTimelogLaborCost = useMemo(
    () => sumJobCostProductionTimelogLabor(timelogsForCalc, services, isTMProject),
    [timelogsForCalc, services, isTMProject]
  );
  const productionTimelogLaborHours = useMemo(
    () => sumJobCostProductionTimelogHours(timelogsForCalc, services),
    [timelogsForCalc, services]
  );
  const effectiveProductionLaborActual = useMemo(
    () => {
      // Backup before production labor tab-priority fix:
      // return (isTMProject
      //   ? Math.max(Number(productFormulaCost1 || 0), productionTimelogLaborCost, Number(laborTabLaborCost || 0))
      //   : Math.max(Number(productFormulaCost1 || 0), productionTimelogLaborCost));
      return resolveEffectiveProductionLabor({
        recipeLabor: productFormulaCost1,
        timelogLabor: productionTimelogLaborCost,
        tabLabor: laborTabLaborCost,
        isTM: isTMProject,
      });
    },
    [productFormulaCost1, productionTimelogLaborCost, laborTabLaborCost, isTMProject]
  );
  const hasProductionRecipeServices = useMemo(
    () =>
      (services || []).some(
        (s) => s?.recipe_id != null && Number(s?.labour_cost_service_id) !== 10
      ),
    [services]
  );

// const productFormulaCost1 = useMemo(() => {
//   return services?.reduce((acc, s) => {
//     if (s?.recipe_id && s?.labour_cost_service_id !== 10 && s?.taken == 1) {
//       const expected = Number(s.originalCost || 0) * Number(s.qty || 0);
//       const actual = parseFloat(s.jobcost_total_cost || 0);
//       const cost = expected > actual ? actual : expected;
//       return acc + cost;
//     }
//     return acc;
//   }, 0).toFixed(2);
// }, [services]);

  const excessLaborCost = 0;
  // const excessLaborCost = useMemo(() => (
  //   Math.max(0, manualTotalPayOut + hourlyTotalPayOut - additionalLaborCost)
  // ), [manualTotalPayOut, hourlyTotalPayOut, additionalLaborCost]);

  // const excessLaborCost1 = useMemo(() => (
  //   Math.max(0, manualTotalPayOut - additionalLaborCost1)
  //   // Math.max(0, manualTotalPayOut + hourlyTotalPayOut - additionalLaborCost1)
  // ), [manualTotalPayOut, hourlyTotalPayOut, additionalLaborCost1]);

  // OLD: excessLaborCost1 — install labor (id 10) in labourExcess doubled Install into Excess / Actual total $130.
  // const excessLaborCost1 = useMemo(() => {
  //   let labourExpected = 0;
  //   let labourActual = 0;
  //   let productionExpected = 0;
  //   let productionActual = 0;
  //   // let otherExpected = 0;
  //   // let otherActual = 0;
  //   let otherExcess = 0;
  //
  //   services.forEach((s) => {
  //     const isTaken = s.taken == 1 || s.billable == 1 || s.is_billable == 1;
  //     if (!isTaken) return;
  //
  //     const unitCost = parseFloat(s.originalCost) || 0;
  //     const expected = parseFloat(unitCost * s.qty) || 0;
  //     const actual = parseFloat(s.jobcost_total_cost) || 0;
  //
  //     if (+s.labour_cost_service_id === 10) {
  //       // install labour
  //       labourExpected += expected;
  //       labourActual += Math.max(actual, installLaborMetrics.cost);
  //     } else if (s.recipe_id && s.labour_cost_service_id !== 10) {
  //       // production labour
  //       productionExpected += expected;
  //       productionActual += actual;
  //     } else if (actual > expected) {
  //       // additional services / other
  //       otherExcess += actual - expected;
  //     }
  //   });
  //
  //   const labourExcess = labourActual > labourExpected ? labourActual - labourExpected : 0;
  //   const productionExcess = productionActual > productionExpected ? productionActual - productionExpected : 0;
  //   // const otherExcess = otherActual > otherExpected ? otherActual - otherExpected : 0;
  //
  //   if (projectData?.project_category === 12) return (0).toFixed(2);
  //   return (
  //     labourExcess +
  //     productionExcess +
  //     otherExcess +
  //     (excessHourlyPayoutAmount || 0) +
  //     (excessManualPayoutAmount || 0)
  //   ).toFixed(2);
  // }, [services, excessHourlyPayoutAmount, excessManualPayoutAmount, installLaborMetrics, isTMProject, projectData?.project_category]);

  const excessLaborCost1 = useMemo(() => {
    let productionExpected = 0;
    let productionActual = 0;
    // let otherExpected = 0;
    // let otherActual = 0;
    let otherExcess = 0;

    services.forEach((s) => {
      const isTaken = s.taken == 1 || s.billable == 1 || s.is_billable == 1;
      if (!isTaken) return;

      // install labour
      if (+s.labour_cost_service_id === 10) return;

      const unitCost = parseFloat(s.originalCost) || 0;
      const expected = parseFloat(unitCost * s.qty) || 0;
      const actual = resolveJobCostServiceLineTotal(s, isTMProject);

      if (s.recipe_id && s.labour_cost_service_id !== 10) {
        // production labour
        productionExpected += expected;
        productionActual += actual;
      } else if (actual > expected) {
        // additional services / other
        otherExcess += actual - expected;
      }
    });

    const productionExcess =
      productionActual > productionExpected ? productionActual - productionExpected : 0;
    // const otherExcess = otherActual > otherExpected ? otherActual - otherExpected : 0;

    // Backup before T&M category type fix: if (projectData?.project_category === 12) return (0).toFixed(2);
    if (isTMProject) return (0).toFixed(2);
    return (
      productionExcess +
      otherExcess +
      (excessHourlyPayoutAmount || 0) +
      (excessManualPayoutAmount || 0)
    ).toFixed(2);
  }, [
    services,
    excessHourlyPayoutAmount,
    excessManualPayoutAmount,
    isTMProject,
    projectData?.project_category
  ]);

// const excessLaborCost1 = useMemo(() => {
//   let labourExpected = 0;
//   let labourActual   = 0;
//   let otherExcess    = 0;

//   services.forEach(r => {
//     const expected = r.taken == 1 ? (parseFloat(r.originalCost * r.qty) || 0) : 0;
//     const actual   = r.taken == 1 ? (parseFloat(r.jobcost_total_cost) || 0) : 0;

//     if (+r.labour_cost_service_id === 10) {
//       // Group totals for labour id 10
//       labourExpected += expected;
//       labourActual   += actual;
//     } else {
//       // Per-row excess calculation for others
//       if (actual > expected) {
//         otherExcess += actual - expected;
//       }
//     }
//   });

//   const labourExcess = labourActual > labourExpected
//     ? labourActual - labourExpected
//     : 0;

//   return +(labourExcess + otherExcess + (excessHourlyPayoutAmount || 0) + (excessManualPayoutAmount || 0)).toFixed(2);
// }, [services, excessHourlyPayoutAmount, excessManualPayoutAmount]);

// OLD: installLaborCost expected — excluded from_job install lines (empty Expected list when Install added on job only).
// const installLaborCost = useMemo(() => {
//   const filtered = services?.filter(
//     s => s?.labour_cost_service_id === 10 && includeRecipeCostServiceRow(s) && Number(s.from_job) !== 1
//   );
//   const expected = filtered.reduce((sum, s) => sum + Number(s.originalCost || 0) * Number(s.qty || 0), 0);
//   return expected.toFixed(2);
// }, [services]);

const installLaborCost = useMemo(() => {
  const filtered = services?.filter(
    (s) => s?.labour_cost_service_id === 10 && includeRecipeCostServiceRow(s)
  );

  const expected = filtered.reduce((sum, s) => {
    // Backup before T&M expected Install Labor markup fix:
    // return sum + Number(s.originalCost || 0) * Number(s.qty || 0);
    return sum + getCompareExpectedServiceUnitCost(s, isTMProject) * Number(s.qty || 0);
  }, 0);

  return expected.toFixed(2);
}, [services, isTMProject]);

const installLaborCost1 = useMemo(() => {
  const installRows = services?.filter(s => Number(s?.labour_cost_service_id) === 10 && includeJobCostServiceRowActual(s, isTMProject)) || [];
  const manualRows = installRows.filter(s => s.jobcost_quantity != null && s.jobcost_quantity !== '' && parseFloat(s.jobcost_quantity) > 0);

  let actual = installLaborMetrics.cost;
  if (manualRows.length > 0) {
    // OLD: sumFromServices used parseFloat(s.jobcost_total_cost) only (no resolveInstallLaborServiceLineCost / T&M base rate).
    const sumFromServices = installRows.reduce(
      (sum, s) => sum + resolveInstallLaborServiceLineCost(s, isTMProject),
      0
    );
    actual = Math.max(installLaborRowCostSum, sumFromServices);
  } else if (!isTMProject && installLaborMetrics.hours <= 0) {
    // OLD: fell back to SO install sell price when no cost-card entry or clock-in.
    actual = 0;
  }

  return actual.toFixed(2);
}, [services, installLaborMetrics.cost, installLaborMetrics.hours, installLaborRowCostSum, isTMProject]);

const grandTotalCost = useMemo(() => {
  const servicesCost = services.reduce((sum, service) => {
    // OLD: if (Number(service.from_job) === 1) return sum;
    if (skipServiceOnExpectedBudgetList(service)) return sum;
    const quantity = parseFloat(service.qty || 0);
    // Backup before T&M expected Install Labor markup fix:
    // const unitCost = parseFloat(service.originalCost || service.unit_cost || 0);
    const unitCost = getCompareExpectedServiceUnitCost(service, isTMProject);
    return sum + quantity * unitCost;
  }, 0);

  return (
    parseFloat(materialPayOut || 0) +
    parseFloat(materialTotal || 0) +
    servicesCost +
    parseFloat(totalStockCost || 0)
  );
}, [materialTotal, services, materialPayOut, totalStockCost, isTMProject]);

const grandTotalCost1 = useMemo(() => {
  return (
    parseFloat(materialPayOut1 || 0) +
    parseFloat(materialTotal1 || 0) +
    parseFloat(totalStockCost1 || 0) +
    parseFloat(additionalLaborCost1 || 0) +
    // OLD: parseFloat(productFormulaCost1 || 0) — missed subtask/workflow timelog production labor (~$11.05).
    parseFloat(effectiveProductionLaborActual || 0) +
    parseFloat(installLaborCost1 || 0) +
    parseFloat(excessLaborCost1 || 0) +
    Number(otherTotals?.cost || 0)
  );
}, [
  materialTotal1,
  materialPayOut1,
  totalStockCost1,
  additionalLaborCost1,
  effectiveProductionLaborActual,
  installLaborCost1,
  excessLaborCost1,
  otherTotals
]);

const grandTotalCost2 = useMemo(() => {
  const selectedProjectsCost = selectedProjects?.filter(i => includeRecipeCostMaterialRow(i))?.reduce(
    (acc, proj) => acc + proj.jobcost_total,                
    0
  );

  const servicesCost = services.reduce((sum, service) => {
    if (projectData?.job_category_name === 'Time & Material') {
      if (service.recipe_id != null && includeRecipeCostServiceRow(service)) {
        return sum + parseFloat(service.jobcost_total_cost || 0);
      }
    } else {
      if (includeRecipeCostServiceRow(service)) {
        return sum + parseFloat(service.jobcost_total_cost || 0);
      }
    }
    return sum;
  }, 0);

  return (
    materialPayOut1 +
    selectedProjectsCost +
    servicesCost 
    +
    Math.max(0, manualTotalPayOut)
  );
}, [selectedProjects, services, materialPayOut1, manualTotalPayOut, hourlyTotalPayOut, projectData?.job_category_name]);

  const compareExpectedTotalCost = Number(grandTotalCost || 0);
  const compareActualTotalCost = Number(grandTotalCost1 || 0);

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object().shape({}),
    onSubmit: () => {}
  });

  // const handleDownloadPDF = () => {
  //   const doc = new jsPDF();
  
  //   doc.setFontSize(16);
  //   doc.text('Expected Budget', 14, 15);
  
  //   // Expected Budget Table
  //   autoTable(doc, {
  //     startY: 20,
  //     head: [['Name', 'Product Formula', 'Qty / Hrs', 'Total']],
  //     body: [
  //       ...selectedProjects.map((proj) => [
  //         proj.name,
  //         proj.recipeName || '',
  //         proj.jobcost_actual_quantity,
  //         fCurrency(
  //           (proj.jobcost_actual_unit_cost || proj.unit_cost) *
  //             proj.jobcost_actual_quantity
  //         ),
  //       ]),
  //       ...selectedProjects1
  //         .filter((d) => d.notFromRecipe !== true)
  //         .map((proj) => [
  //           proj.name,
  //           '',
  //           proj.jobcost_actual_quantity,
  //           fCurrency(
  //             proj.jobcost_actual_unit_cost * proj.jobcost_actual_quantity
  //           ),
  //         ]),
  //       ...services.map((serv) => [
  //         serv.name,
  //         serv.recipeName || '',
  //         serv.qty,
  //         fCurrency(
  //           (serv.qty * (serv.originalCost || serv.unit_cost)).toFixed(2)
  //         ),
  //       ]),
  //     ],
  //     didDrawPage: function (data) {
  //       const summaryStartY = data.cursor.y + 10;
  
  //       doc.setFontSize(12);
  //       doc.text('Additional Material Cost:', 14, summaryStartY);
  //       doc.text(fCurrency(materialPayOut), 170, summaryStartY, { align: 'right' });
  
  //       doc.text('Additional Services:', 14, summaryStartY + 7);
  //       doc.text(fCurrency(additionalLaborCost), 170, summaryStartY + 7, { align: 'right' });
  
  //       doc.text('Actual Product Formula Cost:', 14, summaryStartY + 14);
  //       doc.text(fCurrency(productFormulaCost), 170, summaryStartY + 14, { align: 'right' });
  
  //       doc.text('Excess Labor Cost:', 14, summaryStartY + 21);
  //       doc.text(fCurrency(excessLaborCost), 170, summaryStartY + 21, { align: 'right' });
  
  //       doc.setFontSize(14);
  //       doc.setTextColor(252, 140, 3); // orange
  //       doc.text('Total Cost:', 14, summaryStartY + 30);
  //       doc.text(fCurrency(grandTotalCost), 170, summaryStartY + 30, { align: 'right' });
  
  //       // Start new section
  //       const actualStartY = summaryStartY + 45;
  //       doc.setFontSize(16);
  //       doc.setTextColor(0, 0, 0);
  //       doc.text('Actual Budget', 14, actualStartY);
  
  //       autoTable(doc, {
  //         startY: actualStartY + 5,
  //         head: [['Name', 'Product Formula', 'Qty / Hrs', 'Total']],
  //         body: [
  //           ...selectedProjects
  //             .filter((i) => i.taken === 1)
  //             .map((proj) => [
  //               proj.name,
  //               proj.recipeName || '',
  //               proj.jobcost_quantity,
  //               fCurrency(proj.jobcost_total),
  //             ]),
  //           ...selectedProjects1
  //             .filter((d) => d.notFromRecipe !== true && d.jobcost_quantity > 0)
  //             .map((proj) => [
  //               proj.name,
  //               '',
  //               proj.jobcost_quantity,
  //               fCurrency(proj.jobcost_total),
  //             ]),
  //           ...services
  //             .filter((s) => s.taken === 1)
  //             .map((serv) => [
  //               serv.name,
  //               serv.recipeName || '',
  //               serv.jobcost_quantity,
  //               fCurrency(serv.jobcost_total_cost),
  //             ]),
  //         ],
  //         didDrawPage: function (actualData) {
  //           const actualSummaryY = actualData.cursor.y + 10;
  
  //           doc.setFontSize(12);
  //           doc.setTextColor(0, 0, 0);
  //           doc.text('Additional Material Cost:', 14, actualSummaryY);
  //           doc.text(fCurrency(materialPayOut1), 170, actualSummaryY, { align: 'right' });
  
  //           doc.text('Additional Services:', 14, actualSummaryY + 7);
  //           doc.text(fCurrency(additionalLaborCost1), 170, actualSummaryY + 7, { align: 'right' });
  
  //           doc.text('Actual Product Formula Cost:', 14, actualSummaryY + 14);
  //           doc.text(fCurrency(productFormulaCost1), 170, actualSummaryY + 14, { align: 'right' });
  
  //           doc.text('Excess Labor Cost:', 14, actualSummaryY + 21);
  //           doc.text(fCurrency(excessLaborCost1), 170, actualSummaryY + 21, { align: 'right' });
  
  //           doc.setFontSize(14);
  //           doc.setTextColor(252, 140, 3); // orange
  //           doc.text('Total Cost:', 14, actualSummaryY + 30);
  //           doc.text(
  //             fCurrency(
  //               projectData?.job_category_name === 'Time & Material'
  //                 ? grandTotalCost2
  //                 : grandTotalCost1
  //             ),
  //             170,
  //             actualSummaryY + 30,
  //             { align: 'right' }
  //           );
  
  //           // Save after all tables and summaries
  //           doc.save('Job-Cost-Report.pdf');
  //         },
  //       });
  //     },
  //   });
  // };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  
    doc.setFontSize(14);
    doc.text('Expected Budget', 40, 40);
  
    // Expected Budget Table
    autoTable(doc, {
      startY: 50,
      head: [['Name', 'Product Formula', 'Quantity / Hrs', 'Total']],
      body: [
        ...selectedProjects.map((proj) => [
          proj.name,
          proj.recipeName || '',
          proj.jobcost_actual_quantity,
          // Backup before T&M material markup: fCurrency((proj.jobcost_actual_unit_cost || proj.unit_cost) * proj.jobcost_actual_quantity),
          fCurrency(getJobCostMaterialCost(proj, isTMProject, {
            qty: proj.jobcost_actual_quantity,
            unitCost: proj.jobcost_actual_unit_cost || proj.unit_cost
          })),
        ]),
        ...selectedProjects1
          // Backup before T&M expected material edit fix: .filter((d) => d.notFromRecipe !== true && d.jobcost_actual_quantity > 0)
          .filter((d) => includeMaterialOnExpectedBudget(d, isTMProject))
          .map((proj) => [
            proj.name,
            '',
            proj.jobcost_actual_quantity,
            // Backup before T&M material markup: fCurrency(proj.jobcost_actual_unit_cost * proj.jobcost_actual_quantity),
            fCurrency(getJobCostMaterialCost(proj, isTMProject, {
              qty: proj.jobcost_actual_quantity,
              unitCost: proj.jobcost_actual_unit_cost
            })),
          ]),
          ...stockProducts.map((proj) => [
          proj.product_name,
          proj.recipeName || '',
          proj.location_quantity,
          fCurrency((proj.product_retail_price) * proj.location_quantity),
        ]),
        // ...services.map((serv) => [
          ...services?.reduce((acc, curr) => {

            if (curr.labour_cost_service_id === 10) {
              if (!acc.some(item => item.labour_cost_service_id === 10)) {
                const combined = { ...curr };
                const sameItems = services.filter(s => s.labour_cost_service_id === 10);

                combined.qty = sameItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                combined.jobcost_quantity = sameItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                // Backup before T&M expected Install Labor markup fix:
                // combined.jobcost_total_cost = sameItems.reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0).toFixed(2);
                combined.jobcost_total_cost = sameItems.reduce(
                  (sum, s) => sum + (Number(s.qty || 0) * getCompareExpectedServiceUnitCost(s, isTMProject)),
                  0
                ).toFixed(2);

                acc.push(combined);
              }
            }

            else if (curr.recipe_id != null) {
              const alreadyAdded = acc.some(item => item.__isRecipeGroup);
              if (!alreadyAdded) {
                const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                if (recipeItems.length > 0) {
                  const combined = { ...recipeItems[0] };
                  combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                  combined.jobcost_quantity = recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                  combined.jobcost_total_cost = recipeItems
                  .reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0)
                  .toFixed(2);
                  combined.__isRecipeGroup = true;
                  acc.push(combined);
                }
              }
            }
            else {
              acc.push(curr);
            }

            return acc;
          }, [])
          .map((serv, index) => [
          serv.__isRecipeGroup ? 'Production Labor' : serv.name,
          serv.__isRecipeGroup || serv.labour_cost_service_id === 10 ? '' : serv.recipeName || '',
          serv.qty,
          fCurrency(
            (serv.__isRecipeGroup || serv.labour_cost_service_id === 10)
              ? serv.jobcost_total_cost
              : Number((serv.qty * (serv.originalCost || serv.unit_cost)).toFixed(2))
          )
        ]),
      ],
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: 200
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },
      bodyStyles: {
        textColor: 0
      },
      tableLineColor: 200,
      tableLineWidth: 0.1,
      theme: 'grid'
    });
  
    let posY = doc.lastAutoTable.finalY + 30;
  
    // Summary below Expected
    const expectedSummary = [
      // ['Additional Material Cost', fCurrency(materialTotal)],
          ['Material Cost', fCurrency(
        (Number(materialPayOut || 0) + Number(materialTotal || 0) + Number(totalStockCost || 0)).toFixed(2)
      )],
      ['Additional Services', fCurrency(additionalLaborCost)],
      ['Production Labor', fCurrency(productFormulaCost)],
      ['Install Labor',fCurrency(installLaborCost)],
      // ['Excess Labor Cost', fCurrency(excessLaborCost)],
      ['Total Cost', fCurrency(grandTotalCost)],
    ];
    
    doc.setFontSize(10);
    expectedSummary.forEach(([label, value], i) => {
      const y = posY + i * 18;
      doc.text(label + ':', 350, y);
      doc.text(value, 540, y, { align: 'right' });
    });
  
    // Actual Budget Section
    const actualStartY = posY + 90;
    doc.setFontSize(14);
    doc.text('Actual Budget', 40, actualStartY);
  
    autoTable(doc, {
      startY: actualStartY + 10,
      head: [['Name', 'Product Formula', 'Quantity / Hrs', 'Total']],
      body: [
        // Backup before T&M sales order material actual fix:
        // ...selectedProjects.filter((p) => (includeRecipeCostMaterialRow(p) || p.billable === 1)).map((proj) => [
        ...selectedProjects.filter((p) => includeJobCostMaterialRowActual(p, false)).map((proj) => [
          proj.name,
          proj.recipeName || '',
          proj.jobcost_quantity,
          // Backup before T&M material markup: fCurrency(proj.jobcost_total),
          fCurrency(getJobCostMaterialCost(proj, isTMProject)),
        ]),
        ...selectedProjects1
          // Backup before T&M sales order material actual fix:
          // .filter((d) => d.notFromRecipe !== true && d.jobcost_quantity > 0)
          .filter((d) => d.notFromRecipe !== true && includeJobCostMaterialRowActual(d, false))
          .map((proj) => [
            proj.name,
            '',
            proj.jobcost_quantity,
            // Backup before T&M material markup: fCurrency(proj.jobcost_total),
            fCurrency(getJobCostMaterialCost(proj, isTMProject)),
          ]),
          ...stockProducts.map((proj) => [
          proj.product_name,
          '',
          proj.jobcost_quantity,
          fCurrency((proj.product_retail_price) * proj.jobcost_quantity),
        ]),
        // ...services.filter((s) => s.taken === 1).map((serv) =>
          ...services
              ?.reduce((acc, curr) => {
                // ✅ Case 1: Consolidate install labour
                if (curr.labour_cost_service_id === 10) {
                  const alreadyAdded = acc.some(item => item.labour_cost_service_id === 10);
                  if (!alreadyAdded) {
                    const sameItems = services.filter(s => s.labour_cost_service_id === 10);
                    const combined = { ...curr };

                    const qtyHrs = getInstallLaborQtyHours(sameItems, laborCostTotalFromTimelogs, laborHoursTotalFromTimelogs);
                    combined.qty = qtyHrs;
                    combined.jobcost_quantity = qtyHrs;
                    combined.jobcost_total_cost = getActualInstallLaborJobcostTotal(sameItems, laborCostTotalFromTimelogs, laborHoursTotalFromTimelogs, isTMProject);
                    combined.taken = sameItems.some(s => (includeRecipeCostServiceRow(s) || s.billable === 1)) ? 1 : 0;

                    acc.push(combined);
                  }
                }

                // ✅ Case 2: Consolidate all services with recipe_id (excluding labour)
                else if (curr.recipe_id != null && curr.labour_cost_service_id !== 10) {
                  const alreadyAdded = acc.some(item => item.__isRecipeGroup);
                  if (!alreadyAdded) {
                    const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                    if (recipeItems.length > 0) {
                      const combined = { ...recipeItems[0] };

                      combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                      combined.jobcost_quantity = recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0) + totalManualHours;
                      // Use actual jobcost_total_cost from services, not expected (qty * originalCost)
                      // combined.jobcost_total_cost = (recipeItems.reduce((sum, s) => sum + getServiceActualCost(s), 0) + productionManualPayoutsTotal).toFixed(2);
                      const recipeActual = recipeItems.reduce((sum, s) => sum + getServiceActualCost(s, isTMProject), 0);
                      const manualPart = isTMProject ? 0 : productionManualPayoutsTotal;
                      // Backup before production labor row/summary alignment fix:
                      // combined.jobcost_total_cost = (recipeActual + manualPart).toFixed(2);
                      // combined.jobcost_total_cost = Number(
                      //   resolveEffectiveProductionLabor({
                      //     recipeLabor: recipeActual + manualPart,
                      //     timelogLabor: productionTimelogLaborCost,
                      //     tabLabor: laborTabLaborCost,
                      //     isTM: isTMProject,
                      //   })
                      // ).toFixed(2);
                      combined.jobcost_total_cost = Number(effectiveProductionLaborActual || 0).toFixed(2);
                      combined.taken = recipeItems.some(s => includeJobCostServiceRowActual(s, isTMProject) || s.billable === 1) ? 1 : 0;
                      combined.__isRecipeGroup = true;

                              acc.push(combined);
                            }
                          }
                        }

                        // ✅ Default case: push as-is
                        else {
                          acc.push(curr);
                        }

                        return acc;
                      }, [])
              // Backup before non-T&M actual-only list: .filter(i => (i.taken === 1 || i.billable === 1) || ...)
              .filter((i) => {
                if (isTMProject) {
                  return (i.taken === 1 || i.billable === 1) ||
                    (i.__isRecipeGroup && (totalManualHours > 0 || productionManualPayoutsTotal > 0));
                }
                const rowCost = Number(i.jobcost_total_cost || 0);
                if (i.__isRecipeGroup) {
                  return rowCost > 0 || productionTimelogLaborCost > 0;
                }
                if (Number(i.labour_cost_service_id) === 10) {
                  return rowCost > 0 || laborHoursTotalFromTimelogs > 0;
                }
                return includeJobCostServiceRowActual(i, false);
              })
              .map((serv, index) => 
           [
          serv.__isRecipeGroup ? 'Production Labor' : serv.name,
          serv.__isRecipeGroup ? '' :serv.recipeName || '',
          serv.jobcost_quantity,
          // Backup before non-T&M no-hours display fix: fCurrency(getServiceActualCost(serv)),
          fCurrency(getCompareActualServiceRowCost(serv, effectiveProductionLaborActual, isTMProject)),
        ]),
        // Separate Production Labor row for Time & Material projects
        // ...(projectData?.job_category_name === 'Time & Material' && (totalManualHours > 0 || productionManualPayoutsTotal > 0) ? [[
        ...(projectData?.job_category_name === 'Time & Material' && totalManualHours > 0 ? [[
          'Production Labor',
          '',
          parseFloat(totalManualHours || 0).toFixed(2),
          fCurrency(productionManualPayoutsTotal)
        ]] : []),
      ],
      headStyles: {
        fillColor: [255, 255, 255], // white
        textColor: 0,
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: 200
      },
      bodyStyles: {
        textColor: 0
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },
      tableLineColor: 200,
      tableLineWidth: 0.1,
      theme: 'grid'
    });
  
    const finalY = doc.lastAutoTable.finalY + 30;
  
    const actualSummary = [
      ['Material Cost', fCurrency((parseFloat(materialPayOut1 || 0) + parseFloat(materialTotal1 || 0) + parseFloat(totalStockCost1 || 0)).toFixed(2))],
      ['Additional Services', fCurrency(additionalLaborCost1)],
      ['Production Labor', fCurrency(effectiveProductionLaborActual)],
      ['Install Labor',fCurrency(installLaborCost1)],
      ['Equipment Cost', fCurrency(Number(otherTotals?.cost || 0).toFixed(2))],
      // ['Excess Labor Cost', fCurrency(excessLaborCost1)],
      ['Total Cost', fCurrency(
        projectData?.job_category_name === 'Time & Material' ? grandTotalCost1 : grandTotalCost1  // grandTotalCost2
      )],
    ];
    
    doc.setFontSize(10);
    actualSummary.forEach(([label, value], i) => {
      const y = finalY + i * 18;
      doc.text(label + ':', 350, y);
      doc.text(value, 540, y, { align: 'right' });
    });
  
    doc.save('Job-Cost-Report.pdf');
  };
  
  return (
    <Page title="Job Calculator">
      <Container>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
          <ReactToPrint
                trigger={() => (<Button> <Print /> Print A3</Button>)}
                content={() => componentRef.current}
                copyStyles={false}
                pageStyle={`
                  @media print {
                    body {
                      font-size: 12pt;
                    }
                    table {
                      width: 100%;
                      border-collapse: collapse;
                      margin-bottom: 10px;
                    }

                    th, td {
                      border: 1px solid #ddd;
                      padding: 5px;
                      text-align: left;
                    }
                    #right {
                      text-align: right;
                      margin-top: -200px; 
                    }
                    #total {
                      text-align: right;
                      display:flex;
                      justify-content: flex-end;
                    }
                    #subtotal {
                      text-align: left;
                      
                    }
                    #discount {
                      text-align: left;
                      word-wrap: break-word;
                      margin-right: 100px;
                  }
                  
                    #totalmain {
                      text-align: left;
                      
                    }
                    #subtotal1 {
                      text-align: left;
                      
                    }
                    #discount1 {
                      text-align: left;
                      word-wrap: break-word;
                      margin-right: 30px;
                  }
                  
                    #totalmain1 {
                      text-align: left;
                      
                    }
                    #total1 {
                      text-align: right;
                      display:flex;
                      justify-content: flex-end;
                      margin-top: -220px; 
                      
                    }
                    #logoImage {
                      height: 100px;
                      width:100px;
                    }
                    #order{
                      margin-bottom:-20px
                    }
                    #title{
                      margin-bottom:-20px
                    }
                    #orderID {
                      margin-bottom:-20px
                    }
                    #to {
                      margin-bottom:-20px
                    }
                    
                  }
                `}
              />
          <Button startIcon={<GetApp />} onClick={handleDownloadPDF}>
            Download
          </Button>
        </Box>
         <div style={{ display: "none" }}>
         <PrintableJobCostReport
          selectedProjects={selectedProjects}
          selectedProjects1={selectedProjects1}
          services={services}
          ref={componentRef}
          data={data}
          materialPayOut={materialPayOut}
          materialPayOut1={materialPayOut1}
          additionalLaborCost={additionalLaborCost}
          additionalLaborCost1={additionalLaborCost1}
          productFormulaCost={productFormulaCost}
          productFormulaCost1={effectiveProductionLaborActual}
          excessLaborCost={excessLaborCost}
          excessLaborCost1={excessLaborCost1}
          grandTotalCost={grandTotalCost}
          grandTotalCost1={grandTotalCost1}
          installLaborCost={installLaborCost}
          installLaborCost1={installLaborCost1}
          // grandTotalCost2={grandTotalCost2}
          grandTotalCost2={grandTotalCost1}
          projectData={projectData}
          materialTotal1={materialTotal1}
          materialTotal={materialTotal}
          totalStockCost={totalStockCost}
          totalStockCost1={totalStockCost1}
          otherTotals={otherTotals}
          otherEntries={otherEntries}
          stockProducts={stockProducts}
          totalManualHours={totalManualHours}
          manualHoursCost={manualHoursCost}
          manuallyAddedPayoutsTotal={manuallyAddedPayoutsTotal}
          productionManualPayoutsTotal={productionManualPayoutsTotal}
          laborCostTotalFromTimelogs={laborCostTotalFromTimelogs}
          laborHoursTotalFromTimelogs={laborHoursTotalFromTimelogs}
          installLaborMetrics={installLaborMetrics}
          productionTimelogLaborCost={productionTimelogLaborCost}
          productionTimelogLaborHours={productionTimelogLaborHours}
          laborTabLaborCost={laborTabLaborCost}
          />

        </div>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <Card>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 2, pl: 3 }}>
                <Typography variant="h4" noWrap>
                  Expected Budget
                </Typography>
              </Box>
              <FormikProvider value={formik}>
                <Scrollbar sx={{ height: '320px' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name </TableCell>
                          {/* <TableCell>{projectData?.job_category_name === 'Time & Material' ? '' : 'Product Formula'}</TableCell> */}
                          <TableCell>Product Formula</TableCell>
                          <TableCell>Quantity / Hrs</TableCell>
                          {/* <TableCell>Unit Price</TableCell> */}
                          <TableCell>Total</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProjects?.map((project, index) => (
                          <TableRow key={index}>
                            <TableCell>{project.name || project?.productName || project?.rfpProductName} {project?.barcode ? '' : "(Sub contract)"}</TableCell>
                            <TableCell>{project.recipeName || ''}</TableCell>
                            <TableCell>{getCompareExpectedMaterialQty(project)}</TableCell>
                            {/* <TableCell>{fCurrency((project.jobcost_actual_unit_cost || project.unit_cost))}</TableCell> */}
                            <TableCell>
                              {/* Backup before T&M material markup: {fCurrency((project.jobcost_actual_unit_cost || project.unit_cost) * project.jobcost_actual_quantity)} */}
                              {fCurrency(getJobCostMaterialCost(project, isTMProject, {
                                qty: getCompareExpectedMaterialQty(project),
                                unitCost: getCompareExpectedMaterialUnitCost(project)
                              }))}
                            </TableCell>
                            

                          </TableRow>
                        ))}
                        {/* Backup before T&M expected material edit fix: selectedProjects1?.filter(data=>data?.notFromRecipe != true && data?.jobcost_actual_quantity > 0)?.map */}
                        {selectedProjects1?.filter((data) => includeMaterialOnExpectedBudget(data, isTMProject))?.map((project, index) => (
                          <TableRow key={index}>
                            <TableCell>{project.name}</TableCell>
                            <TableCell>{''}</TableCell>
                            <TableCell>{getCompareExpectedMaterialQty(project)}</TableCell>
                            {/* <TableCell>{fCurrency(project.jobcost_actual_unit_cost)}</TableCell> */}
                            <TableCell>
                              {/* Backup before T&M material markup: {fCurrency((project.jobcost_actual_unit_cost) * project.jobcost_actual_quantity)} */}
                              {fCurrency(getJobCostMaterialCost(project, isTMProject, {
                                qty: getCompareExpectedMaterialQty(project),
                                unitCost: getCompareExpectedMaterialUnitCost(project)
                              }))}
                            </TableCell>
                          </TableRow>
                        ))}
                        {stockProducts?.map((product, index) => (
                          <TableRow key={index}>
                            <TableCell>{product.product_name}</TableCell>
                            <TableCell>{''}</TableCell>
                            <TableCell>{product.location_quantity}</TableCell>
                            <TableCell>
                              {fCurrency((parseFloat(product.product_retail_price || 0)) * parseFloat(product.location_quantity || 0))}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* {services.map((service, index) => ( */}
                        {
                        // services
                        //   ?.reduce((acc, curr) => {
                        //     if (curr.labour_cost_service_id === 10) {
                        //       if (!acc.some(item => item.labour_cost_service_id === 10)) {
                        //         const combined = { ...curr }
                        //         const sameItems = services.filter(s => s.labour_cost_service_id === 10)

                        //         combined.qty = sameItems.reduce((sum, s) => sum + Number(s.qty || 0), 0)
                        //         combined.jobcost_quantity = sameItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0)
                        //         combined.jobcost_total_cost = sameItems.reduce((sum, s) => sum + Number(s.jobcost_total_cost || 0), 0).toFixed(2)

                        //         acc.push(combined)
                        //       }
                        //     } else {
                        //       acc.push(curr)
                        //     }
                        //     return acc
                        //   }, [])
                        //   .map((service, index) => (
                          services?.reduce((acc, curr) => {
                                // Skip services added directly from the job cost page
                                // Skip job-added additional services only; keep install/production on Expected list.
                                // OLD: if (Number(curr.from_job) === 1) return acc;
                                if (skipServiceOnExpectedBudgetList(curr)) return acc;

                                // ✅ Case 1: Consolidate install labour - untouched logic
                                if (curr.labour_cost_service_id === 10) {
                                  if (!acc.some(item => item.labour_cost_service_id === 10)) {
                                    const combined = { ...curr };
                                    const sameItems = services.filter(s => s.labour_cost_service_id === 10);

                                    combined.qty = sameItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                                    combined.jobcost_quantity = sameItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                                    // Backup before T&M expected Install Labor markup fix:
                                    // combined.jobcost_total_cost = sameItems.reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0).toFixed(2);
                                    combined.jobcost_total_cost = sameItems.reduce(
                                      (sum, s) => sum + (Number(s.qty || 0) * getCompareExpectedServiceUnitCost(s, isTMProject)),
                                      0
                                    ).toFixed(2);

                                    acc.push(combined);
                                  }
                                }

                                // Case 2: Consolidate ALL services that have any recipe_id (not null) but NOT labour
                                else if (curr.recipe_id != null) {
                                  const alreadyAdded = acc.some(item => item.__isRecipeGroup);
                                  if (!alreadyAdded) {
                                    const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                                    if (recipeItems.length > 0) {
                                      const combined = { ...recipeItems[0] };
                                      combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                                      combined.jobcost_quantity = recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                                      combined.jobcost_total_cost = recipeItems
                                      .reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0)
                                      .toFixed(2);
                                      combined.__isRecipeGroup = true;
                                      acc.push(combined);
                                    }
                                  }
                                }
                                else {
                                  acc.push(curr);
                                }

                                return acc;
                              }, [])
                              .map((service, index) => (
                          <TableRow key={index}>
                            {
                            console.log('TableRow', service)

                            }
                            <TableCell style={{ color: 'yellow' }}>{service.__isRecipeGroup ? 'Production Labor' : service.name}</TableCell>
                            <TableCell style={{ color: 'yellow' }}>{service.__isRecipeGroup || service.labour_cost_service_id === 10 ? '' : service.recipeName}</TableCell>
                            <TableCell style={{ color: 'yellow' }}>{parseFloat(service.qty || 0).toFixed(2)}</TableCell>
                            {/* <TableCell style={{ color: 'yellow' }}>{fCurrency(service.jobcost_unit_cost|| service.originalCost||service.unit_cost)}</TableCell> */}
                            <TableCell style={{ color: 'yellow' }}>
                              {/* {fCurrency((service.qty * (service.originalCost||service.unit_cost))?.toFixed(2))} */}
                              {/* OLD: __isRecipeGroup || id 10 used getServiceActualCost (actual jobcost) on Expected column. */}
                            {service?.__isRecipeGroup || service.labour_cost_service_id === 10
                              ? fCurrency(
                                  Number(service.jobcost_total_cost) ||
                                    Number(service.qty || 0) *
                                      getCompareExpectedServiceUnitCost(service, isTMProject)
                                )
                              : fCurrency(
                                  (Number(service.qty || 0) *
                                    Number(service.originalCost || service.unit_cost || 0))
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box>
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* {projectData?.job_category_name === 'Time & Material' ? 
                      <Typography variant="h6">Material Cost</Typography>
                    : */}
                      {/* <Typography variant="h6">Additional Material Cost</Typography> */}
                      <Typography variant="h6">Material Cost</Typography>
                    {/* } */}
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(parseFloat(materialPayOut || 0) + parseFloat(materialTotal || 0) + parseFloat(totalStockCost || 0))}</Typography>
                  </TableCell>
                </RowResultStyle>
                {/* {projectData?.job_category_name === 'Time & Material' ? '' : */}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                      {/* <Typography variant="h6">Services Cost</Typography> */}
                      <Typography variant="h6">Additional Services</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(additionalLaborCost)}</Typography>
                  </TableCell>
                </RowResultStyle>
                {/* } */}
                {/* {projectData?.job_category_name === 'Time & Material' ? '' : */}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* <Typography variant="h6">Actual Product Formula Cost</Typography> */}
                    <Typography variant="h6">Production Labor</Typography>

                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(productFormulaCost)}</Typography>
                  </TableCell>
                </RowResultStyle>
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* <Typography variant="h6">Actual Product Formula Cost</Typography> */}
                    <Typography variant="h6">Install Labor</Typography>

                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(installLaborCost)}</Typography>
                  </TableCell>
                </RowResultStyle>
                {/* } */}
                {projectData?.project_category !== 12 && (
                  <RowResultStyle>
                    <TableCell align="right" colSpan={4}>
                      <Typography variant="h6">Excess Labor Cost</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="h6">{fCurrency(excessLaborCost)}</Typography>
                    </TableCell>
                  </RowResultStyle>
                )}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    <Typography variant="h6" sx={{ color: '#fc8c03' }}>Total Cost</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6" sx={{ color: '#fc8c03' }}>{fCurrency(compareExpectedTotalCost)}</Typography>
                  </TableCell>
                </RowResultStyle>
                </Box>
                </Box>
              </FormikProvider>
            </Card>
          </Grid>

          <Grid item md={6} xs={12}>
            <Card>
              <Box sx={{ display: 'flex', alignItems: 'center', py: 2, pl: 3 }}>
                <Typography variant="h4" noWrap>
                  Actual Budget
                </Typography>
              </Box>
              <FormikProvider value={formik}>
                <Scrollbar sx={{ height: '320px' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          {/* <TableCell>{projectData?.job_category_name === 'Time & Material' ? '' : 'Product Formula'}</TableCell> */}
                          <TableCell>Product Formula</TableCell>
                          <TableCell>Quantity / Hrs</TableCell>
                          {/* <TableCell>Unit Price</TableCell> */}
                          <TableCell>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Backup before T&M sales order material actual fix:
                        {selectedProjects?.filter(i => (
                          includeJobCostMaterialRowActual(i, isTMProject) ||
                          (isTMProject && (includeRecipeCostMaterialRow(i) || i.billable == 1))
                        ))?.map((project, index) => ( */}
                        {selectedProjects?.filter(i => (
                          includeJobCostMaterialRowActual(i, false)
                        ))?.map((project, index) => (
                          <TableRow key={index}>
                            <TableCell>{project.name || project?.productName || project?.rfpProductName} {project?.barcode ? '' : "(Sub contract)"}</TableCell>
                            <TableCell>{project.recipeName || ''}</TableCell>
                            <TableCell>{project.jobcost_quantity}</TableCell>
                            {/* <TableCell>{fCurrency(project.jobcost_unit_cost)}</TableCell> */}
                            <TableCell>
                              {/* Backup before T&M material markup: {fCurrency(project.jobcost_total || project?.vendor_price * project?.jobcost_quantity || 0)} */}
                              {fCurrency(getJobCostMaterialCost(project, isTMProject))}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Backup before T&M sales order material actual fix:
                        {selectedProjects1?.filter(data=>(data.notFromRecipe != true && data.jobcost_quantity > 0))?.map((project, index) => ( */}
                        {selectedProjects1?.filter(data=>(data.notFromRecipe != true && includeJobCostMaterialRowActual(data, false)))?.map((project, index) => (
                          <TableRow key={index}>
                            <TableCell>{project.name}</TableCell>
                            <TableCell>{''}</TableCell>
                            <TableCell>{project.jobcost_quantity}</TableCell>
                            {/* <TableCell>{fCurrency(project.jobcost_unit_cost)}</TableCell> */}
                            <TableCell>
                              {/* Backup before T&M material markup: {fCurrency(project.jobcost_total)} */}
                              {fCurrency(getJobCostMaterialCost(project, isTMProject))}
                            </TableCell>
                          </TableRow>
                        ))}
                        {stockProducts?.filter(i => includeRecipeCostStockRow(i)).map((product, index) => (
                        <TableRow key={index}>
                          <TableCell>{product.product_name}</TableCell>
                          <TableCell>{''}</TableCell>
                          <TableCell>{product.jobcost_quantity}</TableCell>
                          <TableCell>
                            {fCurrency((parseFloat(product.product_retail_price || 0)) * parseFloat(product.jobcost_quantity || 0))}
                          </TableCell>
                        </TableRow>
                      ))}
                        {(otherEntries || []).map((entry, index) => {
                          const qty = Number(entry?.quantity || 0);
                          const unitCost = Number(entry?.unit_cost || 0);
                          const rowCost = Number.isFinite(Number(entry?.total_cost))
                            ? Number(entry?.total_cost)
                            : (qty * unitCost);
                          return (
                            <TableRow key={`actual-other-${index}`}>
                              <TableCell>{entry?.description || 'Equipment'}</TableCell>
                              <TableCell>{''}</TableCell>
                              <TableCell>{qty.toFixed(2)}</TableCell>
                              <TableCell>{fCurrency(rowCost)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* {services?.filter(i => i.taken==1)?.map((service, index) => ( */}
                                                  {
                          //                         services
                          // ?.reduce((acc, curr) => {
                          //   if (curr.labour_cost_service_id === 10) {
                          //     if (!acc.some(item => item.labour_cost_service_id === 10)) {
                          //       const combined = { ...curr }
                          //       const sameItems = services.filter(s => s.labour_cost_service_id === 10)

                          //       combined.qty = sameItems.reduce((sum, s) => sum + Number(s.qty || 0), 0)
                          //       combined.jobcost_quantity = sameItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0)
                          //       combined.jobcost_total_cost = sameItems.reduce((sum, s) => sum + Number(s.jobcost_total_cost || 0), 0).toFixed(2)
                          //       combined.taken = sameItems.some(s => s.taken === 1) ? 1 : 0;
                          //       acc.push(combined)
                          //     }
                          //   } else {
                          //     acc.push(curr)
                          //   }
                          //   return acc
                          // }, [])
                          // .filter(i => i.taken === 1)
                          // .map((service, index) => (
                            services
                        ?.reduce((acc, curr) => {
                          // ✅ Case 1: Consolidate install labour
                          if (curr.labour_cost_service_id === 10) {
                            const alreadyAdded = acc.some(item => item.labour_cost_service_id === 10);
                            if (!alreadyAdded) {
                              const sameItems = services.filter(s => s.labour_cost_service_id === 10);
                              const combined = { ...curr };

                              const qtyHrs = getInstallLaborQtyHours(sameItems, installLaborMetrics.cost, installLaborMetrics.hours);
                              combined.qty = qtyHrs;
                              combined.jobcost_quantity = qtyHrs;
                              combined.jobcost_total_cost = getActualInstallLaborJobcostTotal(sameItems, installLaborMetrics.cost, installLaborMetrics.hours, isTMProject);
                              combined.taken = sameItems.some(s => (includeRecipeCostServiceRow(s) || s.billable === 1)) ? 1 : 0;

                              acc.push(combined);
                            }
                          }

                          // ✅ Case 2: Consolidate all services with recipe_id (excluding labour)
                          else if (curr.recipe_id != null && curr.labour_cost_service_id !== 10) {
                            const alreadyAdded = acc.some(item => item.__isRecipeGroup);
                            if (!alreadyAdded) {
                              const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                              if (recipeItems.length > 0) {
                                const combined = { ...recipeItems[0] };

                                const expectedTotal = recipeItems.reduce((sum, s) => sum + (parseFloat(s.originalCost || 0) * parseFloat(s.qty || 0)), 0);
                                const actualTotal = recipeItems.reduce((sum, s) => sum + getServiceActualCost(s, isTMProject), 0);
                                
                                const standardPart = (!isTMProject && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;

                                combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                                combined.jobcost_quantity = productionTimelogLaborHours > 0
                                  ? productionTimelogLaborHours
                                  : recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0) + totalManualHours;
                                // Use capped actual cost matching the summary logic
                                // combined.jobcost_total_cost = (standardPart + productionManualPayoutsTotal).toFixed(2);
                                const manualPart = isTMProject ? 0 : productionManualPayoutsTotal;
                                // Backup before production labor row/summary alignment fix:
                                // combined.jobcost_total_cost = (standardPart + manualPart).toFixed(2);
                                // combined.jobcost_total_cost = Number(
                                //   resolveEffectiveProductionLabor({
                                //     recipeLabor: standardPart + manualPart,
                                //     timelogLabor: productionTimelogLaborCost,
                                //     tabLabor: laborTabLaborCost,
                                //     isTM: isTMProject,
                                //   })
                                // ).toFixed(2);
                                combined.jobcost_total_cost = Number(effectiveProductionLaborActual || 0).toFixed(2);
                                combined.taken = recipeItems.some(s => includeJobCostServiceRowActual(s, isTMProject) || s.billable === 1) ? 1 : 0;
                                combined.__isRecipeGroup = true;

                                acc.push(combined);
                              }
                            }
                          }

                                  // ✅ Default case: push as-is
                                  else {
                                    acc.push(curr);
                                  }

                                  return acc;
                                }, [])
                        // Backup before non-T&M actual-only list: includeRecipeCostServiceRow(i) || i.billable === 1
                        .filter((i) => {
                          if (isTMProject) {
                            return (includeRecipeCostServiceRow(i) || i.billable === 1) ||
                              (i.__isRecipeGroup && (totalManualHours > 0 || productionManualPayoutsTotal > 0));
                          }
                          const rowCost = Number(i.jobcost_total_cost || 0);
                          if (i.__isRecipeGroup) {
                            return rowCost > 0 || productionTimelogLaborCost > 0;
                          }
                          if (Number(i.labour_cost_service_id) === 10) {
                            return rowCost > 0 || installLaborMetrics.hours > 0;
                          }
                          return includeJobCostServiceRowActual(i, false);
                        })
                        .map((service, index) => (
                          <TableRow key={index}>
                            <TableCell style={{ color: 'yellow' }}>{service.__isRecipeGroup ? 'Production Labor' : service.name}</TableCell>
                            <TableCell style={{ color: 'yellow' }}>{service.__isRecipeGroup ? '' : service.recipeName}</TableCell>
                            <TableCell style={{ color: 'yellow' }}>{parseFloat(service.jobcost_quantity || 0).toFixed(2)}</TableCell>
                            {/* <TableCell style={{ color: 'yellow' }}>{fCurrency(service.jobcost_unit_cost|| service.originalCost||service.unit_cost)}</TableCell> */}
                            <TableCell style={{ color: 'yellow' }}>
                              {/* Backup before non-T&M no-hours display fix:
                              {projectData?.job_category_name=='Time & Material' ? 
                                <Typography variant="subtitle2" style={{ color: 'yellow' }}>{fCurrency(getServiceActualCost(service))}</Typography>
                              : (service.labour_cost_service_id == 10 ?
                                <Typography variant="subtitle2" style={{ color: 'yellow' }}>{fCurrency(getServiceActualCost(service))}</Typography>
                              :
                                <Typography variant="subtitle2" style={{ color: 'yellow' }}>{fCurrency(getServiceActualCost(service))}</Typography>
                              )} */}
                              <Typography variant="subtitle2" style={{ color: 'yellow' }}>
                                {fCurrency(getCompareActualServiceRowCost(service, effectiveProductionLaborActual, isTMProject))}
                              </Typography>
                              {/* {fCurrency(((service.jobcost_quantity)*(service.jobcost_unit_cost||service.originalCost||service.unit_cost))?.toFixed(2))} */}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Separate Production Labor row for Time & Material projects */}
                        {/* {projectData?.job_category_name === 'Time & Material' && (totalManualHours > 0 || productionManualPayoutsTotal > 0) && ( */}
                        {projectData?.job_category_name === 'Time & Material' && totalManualHours > 0 && (
                          <TableRow key="production_labor_tm">
                            <TableCell style={{ color: 'yellow' }}>Production Labor</TableCell>
                            <TableCell style={{ color: 'yellow' }}></TableCell>
                            <TableCell style={{ color: 'yellow' }}>{parseFloat(totalManualHours || 0).toFixed(2)}</TableCell>
                            <TableCell style={{ color: 'yellow' }}>
                              <Typography variant="subtitle2" style={{ color: 'yellow' }}>
                                ${parseFloat(productionManualPayoutsTotal || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {productionTimelogLaborCost > 0 && !hasProductionRecipeServices && (
                          <TableRow key="production-timelog-actual">
                            <TableCell style={{ color: 'yellow' }}>Production Labor</TableCell>
                            <TableCell style={{ color: 'yellow' }} />
                            <TableCell style={{ color: 'yellow' }}>
                            {/* {parseFloat(
                                (staffTimelogsGet || []).reduce((sum, log) => {
                                  if (Number(log?.hours) > 0) return sum + Number(log.hours);
                                  const th = Number(log?.total_hours) || 0;
                                  const tm = Number(log?.total_minutes) || 0;
                                  return sum + th + tm / 60;
                                }, 0)
                              ).toFixed(2)} */}
                              {parseFloat(productionTimelogLaborHours || 0).toFixed(2)}
                            </TableCell>
                            <TableCell style={{ color: 'yellow' }}>
                              <Typography variant="subtitle2" style={{ color: 'yellow' }}>
                                {/* Backup before production labor list/summary alignment fix: {fCurrency(productionTimelogLaborCost)} */}
                                {fCurrency(effectiveProductionLaborActual)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box>
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* {projectData?.job_category_name === 'Time & Material' ? 
                      <Typography variant="h6">Material Cost</Typography>
                    : */}
                      {/* <Typography variant="h6">Additional Material Cost</Typography> */}
                      <Typography variant="h6">Material Cost</Typography>
                    {/* } */}
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">
                      {fCurrency((parseFloat(materialPayOut1 || 0) + parseFloat(materialTotal1 || 0) + parseFloat(totalStockCost1 || 0)).toFixed(2))}
                      </Typography>
                  </TableCell>
                </RowResultStyle>
                {/* {projectData?.job_category_name === 'Time & Material' ? "" : */}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* <Typography variant="h6">Services Cost</Typography> */}
                    <Typography variant="h6">Additional Services</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(additionalLaborCost1)}</Typography>
                  </TableCell>
                </RowResultStyle>
                {/* } */}
                {/* {(projectData?.job_category_name === 'Time & Material') ? '' : */}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    <Typography variant="h6">Production Labor</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(effectiveProductionLaborActual)}</Typography>
                  </TableCell>
                </RowResultStyle>
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    {/* <Typography variant="h6">Actual Product Formula Cost</Typography> */}
                    <Typography variant="h6">Install Labor</Typography>

                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(installLaborCost1)}</Typography>
                  </TableCell>
                </RowResultStyle>
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    <Typography variant="h6">Equipment Cost</Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="h6">{fCurrency(Number(otherTotals?.cost || 0).toFixed(2))}</Typography>
                  </TableCell>
                </RowResultStyle>
                {/* } */}
                {projectData?.project_category !== 12 && (
                  <RowResultStyle>
                    <TableCell align="right" colSpan={4}>
                      <Typography variant="h6">Excess Labor Cost</Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="h6">{fCurrency(excessLaborCost1)}</Typography>
                    </TableCell>
                  </RowResultStyle>
                )}
                <RowResultStyle>
                  <TableCell align="right" colSpan={4}>
                    <Typography variant="h6" sx={{ color: '#fc8c03' }}>Total Cost</Typography>
                  </TableCell>
                  <TableCell align="left">
                  {projectData?.job_category_name === 'Time & Material'? 
                    <Typography variant="h6" sx={{ color: '#fc8c03' }}>{fCurrency(compareActualTotalCost)}</Typography>
                    :
                    <Typography variant="h6" sx={{ color: '#fc8c03' }}>{fCurrency(compareActualTotalCost)}</Typography>
                  }
                  </TableCell>
                </RowResultStyle>
                </Box>
                </Box>
              </FormikProvider>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}


export const PrintableJobCostReport = forwardRef(({
  selectedProjects,
  selectedProjects1,
  services,
  materialPayOut,
  materialPayOut1,
  additionalLaborCost,
  additionalLaborCost1,
  productFormulaCost,
  productFormulaCost1,
  excessLaborCost,
  excessLaborCost1,
  grandTotalCost,
  grandTotalCost1,
  grandTotalCost2,
  projectData,
  data,
  installLaborCost,
  installLaborCost1,
  materialTotal1,
  materialTotal,
  totalStockCost1,
  totalStockCost,
  otherTotals = { cost: 0, bill: 0 },
  otherEntries = [],
  stockProducts,
  totalManualHours = 0,
  manualHoursCost = 0,
  manuallyAddedPayoutsTotal = 0,
  productionManualPayoutsTotal = 0,
  laborCostTotalFromTimelogs = 0,
  laborHoursTotalFromTimelogs = 0,
  installLaborMetrics = { cost: 0, bill: 0, hours: 0 },
  productionTimelogLaborCost = 0,
  productionTimelogLaborHours = 0,
  laborTabLaborCost = 0
}, ref) => {
  const theme = useTheme();
  const { COMPANY_LOGO, COMPANY_NAME } = useCompanyProfile()
  const isTMProject =
    // Backup before T&M category type fix: projectData?.project_category === 12 || projectData?.job_category_name === 'Time & Material';
    Number(projectData?.project_category) === 12 || projectData?.job_category_name === 'Time & Material';
  return (
    <div ref={ref} style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>
      <Grid container spacing={3}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6}>
          <Box
            component="img"
            alt="logo"
            src={COMPANY_LOGO}
            id="logoImage"
            sx={{ height: 98 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
          <Typography variant="h2">{COMPANY_NAME}</Typography>
        </Grid>
      </Grid>



        {/* <Grid item xs={12} sm={6} sx={{ mb: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="h2">{COMPANY_NAME}</Typography>
          <Typography variant="h2" sx={{ textTransform: 'uppercase' }}>{data?.company_website}</Typography>
          <Typography variant="h3">
            {data?.address}<br /> {data?.city}<br /> {data?.state}, {data?.zipcode}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Typography variant="body2" sx={{ textTransform: 'uppercase', }}>Telephone: {(data?.phone_country_code || data?.phone) ? `+${data?.phone_country_code}${data?.phone}` : '951-682-9660'}</Typography>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>|</Typography>
            <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{data?.company_fax_number ? `Fax: ${data?.company_fax_number}` : ''}</Typography>
          </Box>
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{data?.company_mail}</Typography>
        </Grid> */}
      </Grid>
      {/* Expected Budget */}
      <Typography variant="h4" gutterBottom>Expected Budget</Typography>
      <Table size="small" style={{ marginBottom: 24 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Product Formula</TableCell>
            <TableCell>Quantity / Hrs</TableCell>
            <TableCell>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedProjects.map((item, i) => (
            <TableRow key={`expected-${i}`}>
              <TableCell>{item.name|| item?.productName || item?.rfpProductName} {item?.barcode ? '' : '(Sub contract)'}</TableCell>
              <TableCell>{item.recipeName || ''}</TableCell>
              <TableCell>{getCompareExpectedMaterialQty(item)}</TableCell>
              <TableCell>
                {/* Backup before T&M material markup: {fCurrency((item.jobcost_actual_unit_cost || item.unit_cost) * item.jobcost_actual_quantity)} */}
                {fCurrency(getJobCostMaterialCost(item, isTMProject, {
                qty: getCompareExpectedMaterialQty(item),
                unitCost: getCompareExpectedMaterialUnitCost(item)
              }))}
              </TableCell>
            </TableRow>
          ))}
          {/* Backup before T&M expected material edit fix: selectedProjects1.filter(d => !d.notFromRecipe && d.jobcost_actual_quantity > 0).map */}
          {selectedProjects1.filter((d) => includeMaterialOnExpectedBudget(d, isTMProject)).map((item, i) => (
            <TableRow key={`expected-extra-${i}`}>
              <TableCell>{item.name}</TableCell>
              <TableCell></TableCell>
              <TableCell>{getCompareExpectedMaterialQty(item)}</TableCell>
              <TableCell>
                {/* Backup before T&M material markup: {fCurrency(item.jobcost_actual_unit_cost * item.jobcost_actual_quantity)} */}
                {fCurrency(getJobCostMaterialCost(item, isTMProject, {
                qty: getCompareExpectedMaterialQty(item),
                unitCost: getCompareExpectedMaterialUnitCost(item)
              }))}
              </TableCell>
            </TableRow>
          ))}
          {stockProducts?.map((product, index) => (
            <TableRow key={index}>
              <TableCell>{product.product_name}</TableCell>
              <TableCell>{''}</TableCell>
              <TableCell>{product.location_quantity}</TableCell>
              <TableCell>
                {fCurrency((parseFloat(product.product_retail_price || 0)) * parseFloat(product.location_quantity || 0))}
              </TableCell>
            </TableRow>
          ))}
{/* {services.map((service, i) => ( */}
          {services?.reduce((acc, curr) => {
                                // Skip services added directly from the job cost page
                                // Skip job-added additional services only; keep install/production on Expected list (print).
                                // OLD: if (Number(curr.from_job) === 1) return acc;
                                if (skipServiceOnExpectedBudgetList(curr)) return acc;

                                // ✅ Case 1: Consolidate install labour - untouched logic
                                if (curr.labour_cost_service_id === 10) {
                                  if (!acc.some(item => item.labour_cost_service_id === 10)) {
                                    const combined = { ...curr };
                                    const sameItems = services.filter(s => s.labour_cost_service_id === 10);

                                    combined.qty = sameItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                                    combined.jobcost_quantity = sameItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                                    // Backup before T&M expected Install Labor markup fix:
                                    // combined.jobcost_total_cost = sameItems.reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0).toFixed(2);
                                    combined.jobcost_total_cost = sameItems.reduce(
                                      (sum, s) => sum + (Number(s.qty || 0) * getCompareExpectedServiceUnitCost(s, isTMProject)),
                                      0
                                    ).toFixed(2);

                                    acc.push(combined);
                                  }
                                }

                                // Case 2: Consolidate ALL services that have any recipe_id (not null) but NOT labour
                                else if (curr.recipe_id != null) {
                                  const alreadyAdded = acc.some(item => item.__isRecipeGroup);
                                  if (!alreadyAdded) {
                                    const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                                    if (recipeItems.length > 0) {
                                      const combined = { ...recipeItems[0] };
                                      combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                                      combined.jobcost_quantity = recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0);
                                      combined.jobcost_total_cost = recipeItems
                                      .reduce((sum, s) => sum + (Number(s.qty || 0) * Number(s.originalCost || s.unit_cost || 0)), 0)
                                      .toFixed(2);
                                      combined.__isRecipeGroup = true;
                                      acc.push(combined);
                                    }
                                  }
                                }
                                else {
                                  acc.push(curr);
                                }

                                return acc;
                              }, [])
                              .map((service, i) => (
            <TableRow key={`expected-service-${i}`}>
              <TableCell>{service.__isRecipeGroup ? 'Production Labor' : service.name}</TableCell>
              <TableCell>{service.__isRecipeGroup || service.labour_cost_service_id === 10 ? '' : service.recipeName}</TableCell>
              <TableCell>{parseFloat(service.qty || 0).toFixed(2)}</TableCell>
              <TableCell>
                {/* {fCurrency(service.qty * (service.originalCost || service.unit_cost))} */}
                 {service?.__isRecipeGroup || service.labour_cost_service_id === 10
                  // Backup before T&M expected Install Labor markup fix:
                  // ? fCurrency(getServiceActualCost(service))
                  ? fCurrency(Number(service.jobcost_total_cost) || (Number(service.qty || 0) * getCompareExpectedServiceUnitCost(service, isTMProject)))
                  : fCurrency((Number(service.qty || 0) * Number(service.originalCost || service.unit_cost || 0)).toFixed(2))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box mb={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end',justifyContent: 'flex-end',textAlign: 'right', }}>
        {/* <Typography><strong>Additional Material Cost:</strong> {fCurrency(materialPayOut)}</Typography> */}
        {/* <Typography><strong>Material Cost:</strong> {fCurrency(materialTotal)}</Typography> */}
        <Typography>
          <strong>Material Cost:</strong>{' '}
          {fCurrency(parseFloat(materialTotal || 0) + parseFloat(totalStockCost || 0))}
        </Typography>
        <Typography><strong>Additional Services:</strong> {fCurrency(additionalLaborCost)}</Typography>
        <Typography><strong>Production Labor:</strong> {fCurrency(productFormulaCost)}</Typography>
        <Typography><strong>Install Labor:</strong> {fCurrency(installLaborCost)}</Typography>
        <Typography><strong>Excess Labor Cost:</strong> {fCurrency(excessLaborCost)}</Typography>
        <Typography><strong>Total Cost:</strong> {fCurrency(grandTotalCost)}</Typography>
      </Box>

      {/* Actual Budget */}
      <Typography variant="h4" gutterBottom>Actual Budget</Typography>
      <Table size="small" style={{ marginBottom: 24 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Product Formula</TableCell>
            <TableCell>Quantity / Hrs</TableCell>
            <TableCell>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedProjects.filter(p => (
            includeJobCostMaterialRowActual(p, isTMProject) ||
            (isTMProject && (includeRecipeCostMaterialRow(p) || p.billable === 1))
          )).map((item, i) => (
            <TableRow key={`actual-${i}`}>
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.recipeName || ''}</TableCell>
              <TableCell>{item.jobcost_quantity}</TableCell>
              <TableCell>
                {/* Backup before T&M material markup: {fCurrency(item.jobcost_total)} */}
                {fCurrency(getJobCostMaterialCost(item, isTMProject))}
              </TableCell>
            </TableRow>
          ))}
          {selectedProjects1.filter(d => !d.notFromRecipe && d.jobcost_quantity > 0).map((item, i) => (
            <TableRow key={`actual-extra-${i}`}>
              <TableCell>{item.name}</TableCell>
              <TableCell></TableCell>
              <TableCell>{item.jobcost_quantity}</TableCell>
              <TableCell>
                {/* Backup before T&M material markup: {fCurrency(item.jobcost_total)} */}
                {fCurrency(getJobCostMaterialCost(item, isTMProject))}
              </TableCell>
            </TableRow>
          ))}
          {stockProducts?.filter(i => includeRecipeCostStockRow(i)).map((product, index) => (
            <TableRow key={index}>
              <TableCell>{product.product_name}</TableCell>
              <TableCell>{''}</TableCell>
              <TableCell>{product.jobcost_quantity}</TableCell>
              <TableCell>
                {fCurrency((parseFloat(product.product_retail_price || 0)) * parseFloat(product.jobcost_quantity || 0))}
              </TableCell>
            </TableRow>
          ))}
          {(otherEntries || []).map((entry, index) => {
            const qty = Number(entry?.quantity || 0);
            const unitCost = Number(entry?.unit_cost || 0);
            const rowCost = Number.isFinite(Number(entry?.total_cost))
              ? Number(entry?.total_cost)
              : (qty * unitCost);
            return (
              <TableRow key={`actual-other-print-${index}`}>
                <TableCell>{entry?.description || 'Equipment'}</TableCell>
                <TableCell></TableCell>
                <TableCell>{qty.toFixed(2)}</TableCell>
                <TableCell>{fCurrency(rowCost)}</TableCell>
              </TableRow>
            );
          })}
          {/* {services.filter(s => s.taken === 1).map((service, i) => ( */}
          {
            services
              ?.reduce((acc, curr) => {
                // ✅ Case 1: Consolidate install labour
                if (curr.labour_cost_service_id === 10) {
                  const alreadyAdded = acc.some(item => item.labour_cost_service_id === 10);
                  if (!alreadyAdded) {
                    const sameItems = services.filter(s => s.labour_cost_service_id === 10);
                    const combined = { ...curr };

                    const qtyHrs = getInstallLaborQtyHours(sameItems, installLaborMetrics.cost, installLaborMetrics.hours);
                    combined.qty = qtyHrs;
                    combined.jobcost_quantity = qtyHrs;
                    combined.jobcost_total_cost = getActualInstallLaborJobcostTotal(sameItems, installLaborMetrics.cost, installLaborMetrics.hours, isTMProject);
                    combined.taken = sameItems.some(s => (s.taken === 1 || s.billable === 1)) ? 1 : 0;

                    acc.push(combined);
                  }
                }

                // ✅ Case 2: Consolidate all services with recipe_id (excluding labour)
                else if (curr.recipe_id != null && curr.labour_cost_service_id !== 10) {
                  const alreadyAdded = acc.some(item => item.__isRecipeGroup);
                  if (!alreadyAdded) {
                    const recipeItems = services.filter(s => s.recipe_id != null && s.labour_cost_service_id !== 10);
                    if (recipeItems.length > 0) {
                      const combined = { ...recipeItems[0] };

                      const expectedTotal = recipeItems.reduce((sum, s) => sum + (parseFloat(s.originalCost || 0) * parseFloat(s.qty || 0)), 0);
                      const actualTotal = recipeItems.reduce((sum, s) => sum + getServiceActualCost(s, isTMProject), 0);
                      
                      const standardPart = (!isTMProject && expectedTotal > 0) ? Math.min(actualTotal, expectedTotal) : actualTotal;

                      combined.qty = recipeItems.reduce((sum, s) => sum + Number(s.qty || 0), 0);
                      combined.jobcost_quantity = productionTimelogLaborHours > 0
                        ? productionTimelogLaborHours
                        : recipeItems.reduce((sum, s) => sum + Number(s.jobcost_quantity || 0), 0) + totalManualHours;
                      // Use capped actual cost matching the summary logic
                      // combined.jobcost_total_cost = (standardPart + productionManualPayoutsTotal).toFixed(2);
                      const manualPart = isTMProject ? 0 : productionManualPayoutsTotal;
                      // Backup before production labor row/summary alignment fix:
                      // combined.jobcost_total_cost = (standardPart + manualPart).toFixed(2);
                      // combined.jobcost_total_cost = Number(
                      //   resolveEffectiveProductionLabor({
                      //     recipeLabor: standardPart + manualPart,
                      //     timelogLabor: productionTimelogLaborCost,
                      //     tabLabor: laborTabLaborCost,
                      //     isTM: isTMProject,
                      //   })
                      // ).toFixed(2);
                      combined.jobcost_total_cost = Number(productFormulaCost1 || 0).toFixed(2);
                      combined.taken = recipeItems.some(s => includeJobCostServiceRowActual(s, isTMProject) || s.billable === 1) ? 1 : 0;
                      combined.__isRecipeGroup = true;

                      acc.push(combined);
                    }
                  }
                }

                        // ✅ Default case: push as-is
                        else {
                          acc.push(curr);
                        }

                        return acc;
                      }, [])
              // Backup before non-T&M actual-only list: .filter(i => (i.taken === 1 || i.billable === 1) || ...)
              .filter((i) => {
                if (isTMProject) {
                  return (i.taken === 1 || i.billable === 1) ||
                    (i.__isRecipeGroup && (totalManualHours > 0 || productionManualPayoutsTotal > 0));
                }
                const rowCost = Number(i.jobcost_total_cost || 0);
                if (i.__isRecipeGroup) {
                  return rowCost > 0 || productionTimelogLaborCost > 0;
                }
                if (Number(i.labour_cost_service_id) === 10) {
                  return rowCost > 0 || installLaborMetrics.hours > 0;
                }
                return includeJobCostServiceRowActual(i, false);
              })
              .map((service, i) => (
            <TableRow key={`actual-service-${i}`}>
              <TableCell>{service.__isRecipeGroup ? 'Production Labor' : service.name}</TableCell>
              <TableCell>{service.__isRecipeGroup ? '' : service.recipeName}</TableCell>
              <TableCell>{parseFloat(service.jobcost_quantity || 0).toFixed(2)}</TableCell>
              <TableCell>
                {/* Backup before non-T&M no-hours display fix: {fCurrency(getServiceActualCost(service))} */}
                {fCurrency(getCompareActualServiceRowCost(service, productFormulaCost1, isTMProject))}
              </TableCell>
            </TableRow>
          ))}
          {/* Separate Production Labor row for Time & Material projects in PDF */}
          {/* {projectData?.job_category_name === 'Time & Material' && (totalManualHours > 0 || productionManualPayoutsTotal > 0) && ( */}
          {projectData?.job_category_name === 'Time & Material' && totalManualHours > 0 && (
            <TableRow key="actual-production-labor-tm">
              <TableCell>Production Labor</TableCell>
              <TableCell></TableCell>
              <TableCell>{parseFloat(totalManualHours || 0).toFixed(2)}</TableCell>
              <TableCell>{fCurrency(productionManualPayoutsTotal)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end',textAlign: 'right', }}>
        <Typography><strong>Material Cost:</strong>
         {/* {fCurrency(materialPayOut1)} */}
         {fCurrency((parseFloat(materialPayOut1 || 0) + parseFloat(materialTotal1 || 0) + parseFloat(totalStockCost1 || 0)).toFixed(2))}
         </Typography>
        <Typography><strong>Additional Services:</strong> {fCurrency(additionalLaborCost1)}</Typography>
        <Typography><strong>Production Labor:</strong> {fCurrency(productFormulaCost1)}</Typography>
        <Typography><strong>Install Labor:</strong> {fCurrency(installLaborCost1)}</Typography>
        <Typography><strong>Equipment Cost:</strong> {fCurrency(Number(otherTotals?.cost || 0).toFixed(2))}</Typography>
        <Typography><strong>Excess Labor Cost:</strong> {fCurrency(excessLaborCost1)}</Typography>
        <Typography ><strong>Total Cost:</strong> {
          fCurrency(projectData?.job_category_name === 'Time & Material' ? grandTotalCost1 : grandTotalCost1)
        }</Typography>
      </Box>
    </div>
  );
});

